import { useState, useEffect, useRef } from "react";
import { uploadToAzure } from "./upload";
import * as XLSX from "xlsx";
import { QRCodeSVG } from "qrcode.react";

const API = "https://ca-psereviewai-backend.yellowsea-80606a88.eastus.azurecontainerapps.io";

// Auto-attach JWT to every API request. One wrapper covers all fetch() calls.
// An explicit Authorization header (MFA enrollment token) is left as-is.
// A 401 with code SESSION_EXPIRED fires "psereview-session-expired" so the app signs out.
const _origFetch = window.fetch.bind(window);
window.fetch = (url, opts = {}) => {
  const isApi = typeof url === "string" && url.startsWith(API);
  try {
    if (isApi) {
      const tok = localStorage.getItem("psereview_token");
      const h = opts.headers || {};
      if (tok && !h.Authorization && !h.authorization) {
        opts = { ...opts, headers: { ...h, Authorization: `Bearer ${tok}` } };
      }
    }
  } catch (e) {}
  const p = _origFetch(url, opts);
  if (isApi && !url.includes("/api/auth/logout")) {
    p.then(res => {
      if (res.status === 401) {
        res.clone().json().then(d => {
          if (d && d.code === "SESSION_EXPIRED") window.dispatchEvent(new Event("psereview-session-expired"));
        }).catch(() => {});
      }
    }).catch(() => {});
  }
  return p;
};

const SEVERITY_COLOR = { CRITICAL:"#FF6B6B", MAJOR:"#FFB347", MINOR:"#64B5F6", INFO:"#A0AEC0" };
const SEVERITY_BG    = { CRITICAL:"#2D1515", MAJOR:"#2D2010", MINOR:"#101D2D", INFO:"#161B22" };

const AGENCIES = [
  ["TXDOT",   "TxDOT — Texas DOT"],
  ["HCFCD",   "HCFCD — Harris County Flood Control District"],
  ["NTTA",    "NTTA — North Texas Tollway Authority"],
  ["COH",     "City of Houston"],
  ["COA",     "City of Austin"],
  ["COD",     "City of Dallas"],
  ["FDOT",    "FDOT — Florida DOT"],
  ["NCDOT",   "NCDOT — North Carolina DOT"],
  ["GDOT",    "GDOT — Georgia DOT"],
  ["VDOT",    "VDOT — Virginia DOT"],
  ["PENNDOT", "PennDOT — Pennsylvania DOT"],
  ["IDOT",    "IDOT — Illinois DOT"],
  ["OTHER",   "Other Agency"],
];

const ROLES = [
  "EOR / Design Engineer",
  "PM / Task Leader",
  "TxDOT / Agency Reviewer",
  "Subconsultant",
  "Contractor",
  "Other",
];

const REFERRAL_SOURCES = [
  "Colleague / Word of mouth",
  "LinkedIn",
  "ASCE / Conference",
  "TxGI / Industry Event",
  "Google Search",
  "Nick Dayal / TGG",
  "Other",
];

export default function App() {
  const [screen, setScreen]               = useState("register");
  const [user, setUser]                   = useState(null);
  const [uploading, setUploading]         = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [reviewing, setReviewing]         = useState(false);
  const [reviewStatus, setReviewStatus]   = useState("");
  const [packageData, setPackageData]     = useState(null);
  const [reviewData, setReviewData]       = useState(null);
  const [reviewStart, setReviewStart]     = useState(null);
  const [documents, setDocuments]         = useState([]);
  const [uploadingDoc, setUploadingDoc]   = useState(false);
  const [feedback, setFeedback]           = useState({ useful: null, fp: 0, missing: "", comments: "" });
  const [feedbackDone, setFeedbackDone]   = useState(false);
  const [findingVotes, setFindingVotes]     = useState({});
  const [checkprintId, setCheckprintId]   = useState(null);
  const [checkprintStatus, setCheckprintStatus] = useState(null);
  const [comments, setComments]           = useState([]);
  const [resolution, setResolution]       = useState(null);
  const [uploadingCheckprint, setUploadingCheckprint] = useState(false);
  const [tcpEnabled, setTcpEnabled]       = useState(false);
  const [tcpDraft, setTcpDraft]           = useState(null);
  const [tcpLoading, setTcpLoading]       = useState(false);
  const [forgotMode, setForgotMode]       = useState(false);
  const [forgotEmail, setForgotEmail]     = useState("");
  const [forgotMsg, setForgotMsg]         = useState("");
  const [resetToken, setResetToken]       = useState("");
  const [resetPw, setResetPw]             = useState("");
  const [resetPw2, setResetPw2]           = useState("");
  const [resetMsg, setResetMsg]           = useState("");
  const [mfaToken, setMfaToken]           = useState(null);  // short-lived token from /login
  const [mfaCode, setMfaCode]             = useState("");
  const [mfaSetup, setMfaSetup]           = useState(null);  // { secret, otpauth_uri }
  const [mfaMsg, setMfaMsg]               = useState("");
  const [commentsFile, setCommentsFile]   = useState(null);
  const [checkprintFile, setCheckprintFile] = useState(null); // 60% checkprint PDF for resolution mode
  const [reviewType, setReviewType]       = useState("fresh"); // "fresh" | "resolution"
  const [deletingPackage, setDeletingPackage] = useState(false);
  const [remapping, setRemapping] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [activeReviewId, setActiveReviewId] = useState(null);
  const [commentsRaw, setCommentsRaw]     = useState([]);   // raw rows from xlsx/csv
  const [colMap, setColMap]               = useState(null); // { sheet, comment, discipline }
  const [showColMapper, setShowColMapper] = useState(false);
  const [spreadsheetHeaders, setSpreadsheetHeaders] = useState([]);
  const [runningResolution, setRunningResolution] = useState(false);
  const [error, setError]                 = useState(null);
  const [reviewsList, setReviewsList]     = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);

  const [login, setLogin] = useState({ email:"", password:"" });
  const [reg, setReg] = useState({
    name:"", firm:"", title:"", email:"",
    agency:"TXDOT", role:"", referral_source:"",
    trial_days:14, data_consent:false, password:"",
  });

  const [scope, setScope] = useState({
    description:"", submittal_stage:"90%", agency:(JSON.parse(localStorage.getItem("psereview_user")||"{}").agency) || "TXDOT",
    additional_agencies: [],
    row_required:null, utilities_involved:null, lighting_required:null,
    phased_construction:null, environmental_clearance:"", design_exceptions:null,
  });

  // Restore session from localStorage
  useEffect(() => {
    fetch(`${API}/api/tcp/enabled`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("psereview_token")}` }
    }).then(r => r.ok ? r.json() : {enabled:false})
      .then(d => setTcpEnabled(!!d.enabled))
      .catch(() => setTcpEnabled(false));
  }, [user]);

  useEffect(() => {
    // Password-reset deep link: /reset?token=... -> show reset screen
    try {
      const t = new URLSearchParams(window.location.search).get("token");
      if (t) { setResetToken(t); setScreen("reset"); }
    } catch {}
  }, []);

  useEffect(() => {
    // Server ended the session (30-min inactivity, 12-h limit, revoked) -> back to sign-in
    const onExpired = () => {
      localStorage.removeItem("psereview_user"); localStorage.removeItem("psereview_token");
      localStorage.removeItem("psereview_screen");
      setUser(null); setPackageData(null); setReviewData(null); setActiveReviewId(null); setReviewing(false);
      setError("Your session expired after 30 minutes of inactivity. Please sign in again.");
      setScreen("login");
    };
    window.addEventListener("psereview-session-expired", onExpired);
    return () => window.removeEventListener("psereview-session-expired", onExpired);
  }, []);

  useEffect(() => {
    // Background polling keeps the server session alive, so also end it after
    // 30 minutes without keyboard/mouse activity in this tab (AC-11).
    if (!user) return;
    let last = Date.now();
    const bump = () => { last = Date.now(); };
    const evts = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    evts.forEach(e => window.addEventListener(e, bump, { passive: true }));
    const iv = setInterval(() => {
      if (Date.now() - last > 30 * 60 * 1000) {
        postLogout();
        window.dispatchEvent(new Event("psereview-session-expired"));
      }
    }, 30000);
    return () => { clearInterval(iv); evts.forEach(e => window.removeEventListener(e, bump)); };
  }, [user]);

  useEffect(() => {
    // Restore user
    const savedUser = localStorage.getItem("psereview_user");
    if (savedUser) {
      try {
        const u = JSON.parse(savedUser);
        setUser(u);
        setScope(s => ({ ...s, agency: u.agency || "TXDOT" }));
        setScreen("upload");
      } catch {}
    } else {
      return; // no user, nothing else to restore
    }

    // Restore package + review state
    const savedPkg = localStorage.getItem("psereview_package");
    const savedReview = localStorage.getItem("psereview_review_id");
    const savedScreen = localStorage.getItem("psereview_screen");

    if (savedPkg) {
      try {
        setPackageData(JSON.parse(savedPkg));
      } catch {}
    }

    // If we have a saved review_id, fetch its current state
    if (savedReview) {
      fetch(`${API}/api/reviews/${savedReview}`)
        .then(r => r.ok ? r.json() : null)
        .then(rd => {
          if (!rd) {
            localStorage.removeItem("psereview_review_id");
            return;
          }
          setReviewData(rd);
          // If still running, set up polling and put us back on the right screen
          if (rd.status === "CANCELLED" || rd.status === "FAILED") {
            // Don't restore terminal-state reviews
            localStorage.removeItem("psereview_review_id");
          } else if (rd.status === "COMPLETE") {
            setScreen("results");
          } else if (rd.status) {
            setReviewing(true);
            setActiveReviewId(savedReview);
            setReviewStatus("Reconnecting to review...");
            setScreen("scope");
            pollReview(savedReview);
          }
        })
        .catch(() => localStorage.removeItem("psereview_review_id"));
    } else if (savedScreen && ["upload","map","scope","results","checkprint"].includes(savedScreen)) {
      setScreen(savedScreen);
    }
  }, []);

  // Persist package data on change
  useEffect(() => {
    if (packageData) localStorage.setItem("psereview_package", JSON.stringify(packageData));
    else localStorage.removeItem("psereview_package");
  }, [packageData]);

  // Persist screen on change (so refresh stays on same screen)
  useEffect(() => {
    if (user && screen !== "register") localStorage.setItem("psereview_screen", screen);
  }, [screen, user]);

  // Persist active review id
  useEffect(() => {
    if (activeReviewId) localStorage.setItem("psereview_review_id", activeReviewId);
    else localStorage.removeItem("psereview_review_id");
  }, [activeReviewId]);

  // Poll review function — defined here so restore-on-load can use it
  function pollReview(reviewId) {
    const poll = async () => {
      try {
        const r = await fetch(`${API}/api/reviews/${reviewId}`);
        if (!r.ok) return;
        const rd = await r.json();
        setReviewData(rd);
        const s = rd.status || "";
        const found = (rd.summary?.critical||0) + (rd.summary?.major||0) + (rd.summary?.minor||0);
        if (s === "PENDING") setReviewStatus("Starting review...");
        else if (s === "RENDERING") setReviewStatus("Rendering PDF pages for vision review...");
        else if (s === "REVIEWING") setReviewStatus("Initialising discipline reviews...");
        else if (s.startsWith("REVIEWING_")) {
          const disc = s.replace("REVIEWING_","").replace(/_/g," ");
          setReviewStatus(`Reviewing ${disc}... (${found} findings so far)`);
        } else {
          setReviewStatus(s);
        }
        if (rd.status === "COMPLETE") {
          setReviewing(false);
          setActiveReviewId(null);
          setScreen("results");
          return;
        }
        if (rd.status === "FAILED") {
          setReviewing(false);
          setActiveReviewId(null);
          setError("Review failed. Please try again.");
          return;
        }
        if (rd.status === "CANCELLED") {
          setReviewing(false);
          setActiveReviewId(null);
          localStorage.removeItem("psereview_review_id");
          setReviewStatus("Review was cancelled");
          return;
        }
        setTimeout(poll, 6000);
      } catch (e) {
        setTimeout(poll, 10000);
      }
    };
    poll();
  }

  async function loadReviewsList() {
    setLoadingReviews(true);
    try {
      const r = await fetch(`${API}/api/reviews/list/all`);
      if (r.ok) {
        const d = await r.json();
        setReviewsList(d.reviews || []);
      }
    } catch (e) {
      console.error("Failed to load reviews:", e);
    } finally {
      setLoadingReviews(false);
    }
  }

  useEffect(() => {
    if (screen === "reviews") {
      loadReviewsList();
      const interval = setInterval(loadReviewsList, 10000);
      return () => clearInterval(interval);
    }
  }, [screen]);

  useEffect(() => {
    if (user) loadReviewsList();
  }, [user]);

  async function openReview(pkg) {
    if (!pkg.review_id) {
      try {
        const r = await fetch(`${API}/api/packages/${pkg.package_id}`);
        if (r.ok) {
          const p = await r.json();
          setPackageData(p);
          setScreen("scope");
        }
      } catch {}
      return;
    }
    try {
      const [pkgRes, revRes] = await Promise.all([
        fetch(`${API}/api/packages/${pkg.package_id}`),
        fetch(`${API}/api/reviews/${pkg.review_id}`)
      ]);
      if (pkgRes.ok) setPackageData(await pkgRes.json());
      if (revRes.ok) {
        const rd = await revRes.json();
        setReviewData(rd);
        if (rd.status === "COMPLETE") {
          setScreen("results");
        } else {
          setActiveReviewId(pkg.review_id);
          setReviewing(true);
          setReviewStatus(rd.status);
          setScreen("scope");
          pollReview(pkg.review_id);
        }
      }
    } catch (e) {
      setError("Failed to open review: " + e.message);
    }
  }

  async function handleRegister() {
    setError(null);
    if (!reg.name || !reg.firm || !reg.email || !reg.title || !reg.role || !reg.agency) {
      setError("All fields marked * are required."); return;
    }
    if (!reg.password || reg.password.length < 12) {
      setError("Please choose a password of at least 12 characters with upper and lower case letters, a number and a symbol."); return;
    }
    if (!reg.data_consent) {
      setError("Please accept the data handling terms to proceed."); return;
    }
    try {
      const res = await fetch(`${API}/api/auth/register`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ email: reg.email, password: reg.password, name: reg.name,
          firm: reg.firm, title: reg.title, agency: reg.agency, role: reg.role }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Registration failed");
      localStorage.setItem("psereview_token", data.token);
      const userData = { ...reg, password: undefined, user_id: data.user_id };
      localStorage.setItem("psereview_user", JSON.stringify(userData));
      setUser(userData);
      setScope(s => ({ ...s, agency: reg.agency }));
      setScreen("upload");
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleForgot() {
    setForgotMsg("");
    if (!forgotEmail) { setForgotMsg("Enter your email."); return; }
    try {
      const res = await fetch(`${API}/api/auth/forgot-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await res.json();
      setForgotMsg(data.message || "If an account exists, a reset link has been sent.");
    } catch {
      setForgotMsg("If an account exists, a reset link has been sent.");
    }
  }

  async function handleReset() {
    setResetMsg("");
    if (!resetPw || resetPw.length < 12) { setResetMsg("Password must be at least 12 characters with upper and lower case letters, a number and a symbol."); return; }
    if (resetPw !== resetPw2) { setResetMsg("Passwords do not match."); return; }
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: resetToken, password: resetPw }),
      });
      const data = await res.json();
      if (!res.ok) { setResetMsg(data.detail || "Reset failed."); return; }
      setResetMsg("Password reset. Redirecting to sign in…");
      setTimeout(() => {
        window.history.replaceState({}, "", "/");
        setScreen("login"); setForgotMode(false);
      }, 1600);
    } catch {
      setResetMsg("Reset failed. The link may have expired.");
    }
  }

  async function handleLogin() {
    setError(null);
    if (!login.email || !login.password) { setError("Enter your email and password."); return; }
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ email: login.email, password: login.password }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.code === "PASSWORD_CHANGE_REQUIRED") { setForgotMode(true); setForgotEmail(login.email); }
        throw new Error(data.detail || "Login failed");
      }
      setLogin({ ...login, password: "" });
      if (data.mfa_required) {
        setMfaToken(data.mfa_token); setMfaCode(""); setMfaMsg(""); setScreen("mfa"); return;
      }
      if (data.mfa_enrollment_required) {
        setMfaToken(data.mfa_token); setScreen("mfa_enroll"); startMfaEnroll(data.mfa_token); return;
      }
      completeLogin(data);
    } catch (err) {
      setError(err.message);
    }
  }

  // Store the session exactly as a password-only login does
  function completeLogin(data) {
    localStorage.setItem("psereview_token", data.token);
    const u = { name: data.name, email: data.email, user_id: data.user_id,
                agency: data.agency || "TXDOT", trial_expires_at: data.trial_expires_at,
                is_admin: !!data.is_admin, mfa_enabled: !!data.mfa_enabled };
    localStorage.setItem("psereview_user", JSON.stringify(u));
    setMfaToken(null); setMfaCode(""); setMfaSetup(null); setMfaMsg(""); setError(null);
    setUser(u);
    setScope(s => ({ ...s, agency: u.agency }));
    setScreen("upload");
  }

  async function handleMfaVerify() {
    setMfaMsg("");
    try {
      const res = await fetch(`${API}/api/auth/mfa/verify`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ mfa_token: mfaToken, code: mfaCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        if ((data.detail || "") === "Token expired") { setMfaToken(null); setScreen("login"); setError("Sign-in timed out. Please sign in again."); return; }
        setMfaMsg(data.detail || "Verification failed."); setMfaCode(""); return;
      }
      completeLogin(data);
    } catch {
      setMfaMsg("Verification failed. Please try again.");
    }
  }

  // tok = enrollment token from login (admins); null = signed-in user (session token is attached automatically)
  async function startMfaEnroll(tok) {
    setMfaSetup(null); setMfaCode(""); setMfaMsg("");
    try {
      const res = await fetch(`${API}/api/auth/mfa/enroll`, {
        method:"POST", headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      const data = await res.json();
      if (!res.ok) { setMfaMsg(data.detail || "Could not start two-factor setup."); return; }
      setMfaSetup(data);
    } catch {
      setMfaMsg("Could not start two-factor setup.");
    }
  }

  async function handleMfaConfirm() {
    setMfaMsg("");
    try {
      const res = await fetch(`${API}/api/auth/mfa/confirm`, {
        method:"POST",
        headers: { "Content-Type":"application/json", ...(mfaToken ? { Authorization: `Bearer ${mfaToken}` } : {}) },
        body: JSON.stringify({ code: mfaCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setMfaMsg(data.detail || "That code is not valid."); setMfaCode(""); return; }
      if (data.token) { completeLogin(data); return; }
      const u = { ...user, mfa_enabled: true };
      localStorage.setItem("psereview_user", JSON.stringify(u));
      setUser(u); setMfaSetup(null); setMfaCode(""); setScreen("upload");
    } catch {
      setMfaMsg("Could not confirm the code. Please try again.");
    }
  }

  // Revoke the server-side session; fire-and-forget (token is read before storage is cleared)
  function postLogout() {
    try { fetch(`${API}/api/auth/logout`, { method:"POST", keepalive:true }).catch(() => {}); } catch {}
  }

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setError(null);
    setUploadProgress("Connecting to secure storage...");
    try {
      const urlRes = await fetch(`${API}/api/upload/generate-upload-url`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          filename: file.name,
          project_name: file.name.replace(".pdf",""),
          review_type: reviewType,
          agency: scope.agency,
          additional_agencies: scope.additional_agencies || []
        }),
      });
      if (!urlRes.ok) throw new Error(await urlRes.text());
      const { upload_url, blob_name, package_id } = await urlRes.json();

      setUploadProgress(`Uploading ${(file.size/1024/1024).toFixed(1)} MB...`);
      await uploadToAzure(upload_url, file, (pct) => {
        setUploadProgress(`Uploading ${(file.size/1024/1024).toFixed(1)} MB... ${pct}%`);
      });

      setUploadProgress("Starting AI analysis...");
      await new Promise(r => setTimeout(r,2000));
      await fetch(`${API}/api/upload/process-uploaded`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ package_id, blob_name }),
      });

      setUploadProgress("Mapping package — please wait...");
      let result;
      for (let i = 0; i < 360; i++) {
        await new Promise(r => setTimeout(r,5000));
        const poll = await fetch(`${API}/api/upload/status/${package_id}`);
        result = await poll.json();
        if (result.status === "MAPPED") break;
        if (result.status === "FAILED") throw new Error("Processing failed");
        setUploadProgress(`Mapping package... (${Math.round((i+1)*5/60)} min)`);
      }
      if (result.status !== "MAPPED") throw new Error("Processing timed out");
      setPackageData(result);
      setDocuments([]);
      setScreen("map");
      // Active review check disabled — use Stop button during active session only
    } catch (err) {
      setError("Upload failed: " + err.message);
    } finally {
      setUploading(false); setUploadProgress("");
    }
  }

  async function handleDocUpload(e, docType) {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      // Step 1: Get SAS URL from backend
      const urlRes = await fetch(`${API}/api/documents/generate-upload-url`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filename: file.name,
          doc_type: docType,
          package_id: packageData?.package_id || null
        })
      });
      if (!urlRes.ok) throw new Error("Failed to get upload URL");
      const { upload_url, blob_name, document_id } = await urlRes.json();

      // Step 2: Upload directly to Azure Blob (chunked for large files)
      await uploadToAzure(upload_url, file, null);

      // Step 3: Confirm upload and store metadata in DB
      const confirmRes = await fetch(`${API}/api/documents/confirm-upload`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          document_id, blob_name,
          filename: file.name,
          doc_type: docType,
          package_id: packageData?.package_id || null,
          size_bytes: file.size
        })
      });
      if (!confirmRes.ok) throw new Error("Failed to confirm upload");
      const doc = await confirmRes.json();
      setDocuments(prev => [...prev, doc]);
    } catch (err) {
      setError("Document upload failed: " + err.message);
    } finally {
      setUploadingDoc(false); e.target.value = "";
    }
  }

  async function deleteAndReupload() {
    if (!packageData?.package_id) return;
    if (!window.confirm("Delete this package? Map and review results will be removed.")) return;
    setDeletingPackage(true);
    try {
      await fetch(`${API}/api/packages/${packageData.package_id || packageData.id}`, { method: "DELETE" });
      setPackageData(null); setReviewData(null); setScreen("upload");
    } catch(e) { alert("Delete failed: " + e.message); }
    finally { setDeletingPackage(false); }
  }

  async function remapPackage() {
    if (!packageData?.package_id) return;
    setRemapping(true);
    try {
      const r = await fetch(`${API}/api/packages/${packageData.package_id || packageData.id}/remap`, { method: "POST" });
      if (!r.ok) throw new Error(await r.text());
      const d = await r.json();
      // Merge new map data — preserve all existing packageData fields
      setPackageData(prev => ({
        ...prev,
        package_map: {
          ...prev.package_map,
          ...d.package_map,
          segments: d.package_map.segments,
          sheet_count: d.package_map.sheet_count,
          segment_count: d.package_map.segment_count,
          confidence_score: d.package_map.confidence_score,
        }
      }));
    } catch(e) { alert("Re-map failed: " + e.message); }
    finally { setRemapping(false); }
  }

  async function stopReview() {
    const rid = activeReviewId || reviewData?.review_id;
    if (!rid) return;
    setStopping(true);
    try {
      await fetch(`${API}/api/reviews/${rid}/cancel`, { method: "POST" });
      setReviewing(false);
      setActiveReviewId(null);
    } catch(e) { alert("Stop failed: " + e.message); }
    finally { setStopping(false); }
  }

  async function handleReview() {
    setReviewing(true); setError(null); setReviewStart(Date.now());
    try {
      const scopeParts = [];
      if (scope.description) scopeParts.push(scope.description);
      if (scope.submittal_stage) scopeParts.push(`${scope.submittal_stage} submittal`);
      scopeParts.push(`Agency: ${scope.agency}`);
      if (scope.row_required === false) scopeParts.push("No ROW acquisition required");
      if (scope.row_required === true)  scopeParts.push("ROW acquisition required");
      if (scope.utilities_involved === false) scopeParts.push("No utility conflicts or relocations");
      if (scope.utilities_involved === true)  scopeParts.push("Utility conflicts/relocations involved");
      if (scope.lighting_required === false) scopeParts.push("No lighting required");
      if (scope.lighting_required === true)  scopeParts.push("Lighting required");
      if (scope.phased_construction === false) scopeParts.push("Single phase construction");
      if (scope.phased_construction === true)  scopeParts.push("Phased construction");
      if (scope.environmental_clearance) scopeParts.push(`Environmental: ${scope.environmental_clearance}`);
      if (scope.design_exceptions === false) scopeParts.push("No design exceptions");
      if (scope.design_exceptions === true)  scopeParts.push("Design exceptions granted");
      const scopeText = scopeParts.join(". ");

      const res = await fetch(`${API}/api/reviews/start`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ package_id: packageData.package_id, scope: scopeText }),
      });
      if (!res.ok) throw new Error(await res.text());
      const { review_id } = await res.json();
      setActiveReviewId(review_id);

      let result;
      let consecutiveErrors = 0;
      setReviewStatus("Starting review...");
      for (let i = 0; i < 720; i++) {
        await new Promise(r => setTimeout(r,5000));
        try {
          const poll = await fetch(`${API}/api/reviews/${review_id}`);
          if (!poll.ok) {
            consecutiveErrors++;
            setReviewStatus(`Reconnecting... (transient ${poll.status})`);
            if (consecutiveErrors >= 12) throw new Error(`Backend unreachable after ${consecutiveErrors} attempts`);
            continue;
          }
          consecutiveErrors = 0;
          result = await poll.json();
          const s = result.status || "";
          if (s === "RENDERING") setReviewStatus("Rendering PDF pages for vision review...");
          else if (s === "REVIEWING") setReviewStatus("Initialising discipline reviews...");
          else if (s.startsWith("REVIEWING_")) {
            const disc = s.replace("REVIEWING_","").replace(/_/g," ");
            const found = (result.summary?.critical||0) + (result.summary?.major||0) + (result.summary?.minor||0);
            setReviewStatus(`Reviewing ${disc}... (${found} findings so far)`);
          }
          if (result.status === "COMPLETE" || result.status === "FAILED" || result.status === "CANCELLED") break;
        } catch (pollErr) {
          consecutiveErrors++;
          if (consecutiveErrors >= 12) throw pollErr;
          setReviewStatus(`Reconnecting... (${consecutiveErrors}/12)`);
        }
      }
      setReviewData(result);
      setActiveReviewId(null);

      // Submit analytics automatically
      
      await fetch(`${API}/api/trial/analytics`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          review_id: result.review_id,
          user_email: user?.email || "",
          agency: scope.agency,
          project_type: packageData?.package_map?.project_type || "",
          package_size_mb: packageData?.size_mb || 0,
          page_count: packageData?.page_count || 0,
          critical_count: result.summary?.critical || 0,
          major_count: result.summary?.major || 0,
          minor_count: result.summary?.minor || 0,
          review_duration_seconds: reviewStart ? Math.round((Date.now() - reviewStart) / 1000) : 0,
          scope_provided: !!scope.description,
          documents_uploaded: documents.length,
        }),
      }).catch(() => {}); // Fire and forget

      setScreen("results");
    } catch (err) {
      setError("Review failed: " + err.message);
    } finally {
      setReviewing(false);
    }
  }

  async function handleCheckprintUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingCheckprint(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("package_id", packageData?.package_id || "");
      form.append("submittal_stage", "60%");
      const res = await fetch(`${API}/api/module6/upload-checkprint?package_id=${packageData?.package_id || ""}`, {
        method: "POST", body: form
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setCheckprintId(data.checkprint_id);
      setCheckprintStatus("PROCESSING");

      // Poll for completion
      for (let i = 0; i < 360; i++) {
        await new Promise(r => setTimeout(r, 5000));
        const poll = await fetch(`${API}/api/module6/status/${data.checkprint_id}`);
        const status = await poll.json();
        setCheckprintStatus(status.status);
        if (status.status === "READY") {
          // Fetch comments
          const cres = await fetch(`${API}/api/module6/comments/${data.checkprint_id}`);
          const cdata = await cres.json();
          setComments(cdata.comments || []);
          break;
        }
      }
    } catch (err) {
      setError("Checkprint upload failed: " + err.message);
    } finally {
      setUploadingCheckprint(false);
      e.target.value = "";
    }
  }

  function handleSpreadsheetUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setCommentsFile(file);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: "binary" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
      if (rows.length < 2) return;
      const headers = rows[0].map((h, i) => h ? String(h).trim() : `Column ${i+1}`);
      const dataRows = rows.slice(1).filter(r => r.some(cell => cell !== undefined && cell !== ""));
      setSpreadsheetHeaders(headers);
      setCommentsRaw(dataRows);
      const detect = (keywords, avoid = []) => {
        const idx = headers.findIndex(h => {
          const l = h.toLowerCase();
          return keywords.some(k => l.includes(k)) && !avoid.some(a => l.includes(a));
        });
        return idx >= 0 ? String(idx) : "";
      };
      const isNumberCol = (h) => /\b(no|#|number|id)\.?$/i.test(h.trim()) || /^(no|#|item)\b/i.test(h.trim());
      const numIdx = headers.findIndex(isNumberCol);
      setColMap({
        number:     numIdx >= 0 ? String(numIdx) : "",
        sheet:      detect(["sheet", "drawing", "dwg", "reference", "plan"], ["response"]),
        comment:    (() => {
          const i = headers.findIndex((h, j) => j !== numIdx &&
            ["comment", "remark", "description", "finding"].some(k => h.toLowerCase().includes(k)) &&
            !["response", "by", "reply", "no", "#", "number"].some(a => h.toLowerCase().split(/[^a-z#]+/).includes(a)));
          return i >= 0 ? String(i) : detect(["comment", "remark", "note"], ["response"]);
        })(),
        discipline: detect(["discipline", "disc", "category", "type", "dept"]),
        response:   detect(["response", "reply", "disposition", "resolution", "designer", "action taken"]),
      });
      setShowColMapper(true);
    };
    reader.readAsBinaryString(file);
  }

  function buildMergedComments() {
    if (!commentsRaw.length || !colMap) return [];
    const col = (row, key) => colMap[key] !== undefined && colMap[key] !== ""
      ? String(row[parseInt(colMap[key])] ?? "").trim() : "";
    return commentsRaw.map((row, i) => ({
      source:     "spreadsheet",
      index:      i + 1,
      number:     col(row, "number") || String(i + 1),
      response:   col(row, "response"),
      sheet_ref:  colMap.sheet !== ""      ? String(row[parseInt(colMap.sheet)]      || "").trim() : "",
      comment:    colMap.comment !== ""    ? String(row[parseInt(colMap.comment)]    || "").trim() : "",
      discipline: colMap.discipline !== "" ? String(row[parseInt(colMap.discipline)] || "").trim() : "",
    })).filter(r => r.comment);
  }

  async function handleRunResolution() {
    if (!checkprintId && comments.length === 0) return;
    setRunningResolution(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/module6/verify-resolution`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          package_id: packageData?.package_id || "",
          checkprint_id: checkprintId || null,
          spreadsheet_comments: buildMergedComments(),
          review_type: reviewType,
        })
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      if (!data.job_id) { setResolution(data); return; }   // older backend answered directly
      // The check runs in the background (several minutes); poll for the result.
      const pkg = packageData?.package_id || "";
      let done = false;
      for (let i = 0; i < 540 && !done; i++) {             // up to 45 minutes
        await new Promise(r => setTimeout(r, 5000));
        let js;
        try {
          const poll = await fetch(`${API}/api/module6/resolution-job/${pkg}/${data.job_id}`);
          if (!poll.ok) continue;                          // brief network hiccup: keep polling
          js = await poll.json();
        } catch { continue; }
        if (js.status === "DONE") { setResolution(js.result); done = true; }
        else if (js.status === "FAILED") throw new Error(js.error || "the check failed on the server");
      }
      if (!done) throw new Error("still running after 45 minutes — refresh later or run again");
    } catch (err) {
      setError("Resolution check failed: " + err.message);
    } finally {
      setRunningResolution(false);
    }
  }

  async function handleFindingVote(findingId, vote) {
    setFindingVotes(prev => ({ ...prev, [findingId]: vote }));
    try {
      await fetch(`${API}/api/trial/finding-feedback`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          finding_id: findingId,
          review_id: reviewData?.review_id,
          user_email: user?.email || "",
          vote, // "confirm" | "false_positive" | "partial"
        }),
      });
    } catch {}
  }

  async function handleFeedback() {
    try {
      await fetch(`${API}/api/trial/feedback`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          review_id: reviewData?.review_id,
          user_email: user?.email || "",
          useful: feedback.useful,
          false_positive_count: feedback.fp,
          missing_findings: feedback.missing,
          general_comments: feedback.comments,
        }),
      });
      setFeedbackDone(true);
    } catch {}
  }

  const navItems = [
    { id:"upload",  label:"Upload",         disabled: !user },
    { id:"reviews", label:"My Reviews",     disabled: !user || reviewsList.length === 0 },
    { id:"map",     label:"Package Map",    disabled: !packageData },
    { id:"scope",   label:"Project Scope",  disabled: !packageData },
    { id:"results", label:"Review Results", disabled: !reviewData },
    { id:"checkprint", label:"Comment Resolution", disabled: !packageData || reviewType === "fresh" },
  ];

  return (
    <div style={{ minHeight:"100vh", background:"#0D1117", color:"#E6EDF3",
                  fontFamily:"system-ui, sans-serif", fontSize:15 }}>

      {/* Header */}
      <div style={{ background:"#161B22", borderBottom:"2px solid #30363D",
                    padding:"0 32px", display:"flex", alignItems:"center", gap:16, height:60 }}>
        <span style={{ fontWeight:800, fontSize:20 }}><span style={{ color:"#FF4444" }}>PSEReview</span><span style={{ color:"#58A6FF" }}>AI</span></span>
        <span style={{ color:"#8B949E", fontSize:14 }}>The Geotech Guys LLC</span>
        {user && (
          <span style={{ color:"#484F58", fontSize:13, marginLeft:8 }}>
            {user.name} · {user.firm}
          </span>
        )}
        <div style={{ marginLeft:"auto", display:"flex", gap:6 }}>
          {screen !== "register" && navItems.map(item => (
            <button key={item.id} disabled={item.disabled}
              onClick={() => !item.disabled && setScreen(item.id)}
              style={{
                background: screen===item.id ? "#21262D" : "transparent",
                color: item.disabled ? "#484F58" : screen===item.id ? "#E6EDF3" : "#C9D1D9",
                border:`1px solid ${screen===item.id ? "#58A6FF" : "transparent"}`,
                borderRadius:6, padding:"6px 14px", fontSize:14,
                fontWeight: screen===item.id ? 600 : 400,
                cursor: item.disabled ? "not-allowed" : "pointer",
              }}>{item.label}</button>
          ))}
          {user && !user.is_admin && !user.mfa_enabled && screen !== "mfa_enroll" && (
            <button onClick={() => { setMfaToken(null); setScreen("mfa_enroll"); startMfaEnroll(null); }}
              style={{ background:"transparent", color:"#58A6FF", border:"1px solid #30363D",
                       borderRadius:6, padding:"6px 10px", fontSize:13, cursor:"pointer", marginLeft:8 }}>
              Enable two-factor authentication
            </button>
          )}
          {user && (
            <button onClick={() => { postLogout(); localStorage.removeItem("psereview_user"); localStorage.removeItem("psereview_token"); setUser(null); setScreen("register"); setPackageData(null); setReviewData(null); }}
              style={{ background:"transparent", color:"#484F58", border:"none",
                       fontSize:13, cursor:"pointer", marginLeft:8 }}>
              Sign out
            </button>
          )}
        </div>
      </div>

      <div style={{ maxWidth:980, margin:"0 auto", padding:"40px 24px" }}>
        {error && (
          <div style={{ background:"#2D1515", border:"1px solid #FF6B6B", borderRadius:8,
                        padding:"14px 18px", marginBottom:24, color:"#FF9999", fontSize:14 }}>
            {error}
          </div>
        )}

        {/* ── REGISTRATION ── */}
        {screen === "register" && (
          <div style={{ maxWidth:600, margin:"0 auto", paddingTop:40 }}>
            <div style={{ textAlign:"center", marginBottom:36 }}>
              <div style={{ fontSize:30, fontWeight:700, marginBottom:8 }}>
                PSEReviewAI
              </div>
              <div style={{ color:"#A0AEC0", fontSize:15 }}>
                AI-powered PS&E plan review for TxDOT and other agencies.<br/>
                Free trial access — no credit card required.
              </div>
            </div>

            <div style={{ background:"#161B22", border:"1px solid #30363D",
                          borderRadius:10, padding:32 }}>
              <div style={{ fontSize:16, fontWeight:700, marginBottom:24,
                            color:"#E6EDF3" }}>Request Trial Access</div>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:18,
                            marginBottom:18 }}>
                <Field label="Full Name *" value={reg.name}
                  onChange={v => setReg({...reg, name:v})} placeholder="Jane Smith" />
                <Field label="Firm / Company *" value={reg.firm}
                  onChange={v => setReg({...reg, firm:v})} placeholder="HNTB Corporation" />
                <Field label="Title *" value={reg.title}
                  onChange={v => setReg({...reg, title:v})} placeholder="Project Engineer" />
                <Field label="Work Email *" value={reg.email} type="email"
                  onChange={v => setReg({...reg, email:v})} placeholder="you@firm.com" />
                <Field label="Password * (min 12 characters, upper/lower/number/symbol)" value={reg.password} type="password"
                  onChange={v => setReg({...reg, password:v})} placeholder="Choose a password" />
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:18,
                            marginBottom:18 }}>
                <div>
                  <label style={labelStyle}>Primary Agency *</label>
                  <select value={reg.agency} onChange={e => setReg({...reg, agency:e.target.value})}
                    style={selectStyle}>
                    {AGENCIES.map(([v,l]) => <option key={v} value={v} style={{background:"#0D1117"}}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Your Role *</label>
                  <select value={reg.role} onChange={e => setReg({...reg, role:e.target.value})}
                    style={selectStyle}>
                    <option value="" disabled>Select role *</option>
                    {ROLES.map(r => <option key={r} value={r} style={{background:"#0D1117"}}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Trial Duration</label>
                  <select value={reg.trial_days} onChange={e => setReg({...reg, trial_days:parseInt(e.target.value)})}
                    style={selectStyle}>
                    <option value={15}>15 days</option>
                    <option value={14}>14 days</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>How did you hear about us?</label>
                  <select value={reg.referral_source} onChange={e => setReg({...reg, referral_source:e.target.value})}
                    style={selectStyle}>
                    <option value="">Select source</option>
                    {REFERRAL_SOURCES.map(r => <option key={r} value={r} style={{background:"#0D1117"}}>{r}</option>)}
                  </select>
                </div>
              </div>

              {/* Data consent */}
              <div style={{ background:"#0D1117", border:"1px solid #30363D",
                            borderRadius:8, padding:16, marginBottom:24 }}>
                <div style={{ fontSize:13, color:"#A0AEC0", lineHeight:1.7, marginBottom:12 }}>
                  <strong style={{color:"#E6EDF3"}}>Data Handling:</strong> PSEReviewAI processes
                  your uploaded PS&E PDFs using AI to generate review comments. Your documents
                  are stored in Microsoft Azure data centers in the United States. To produce a
                  review, document pages are processed by Anthropic's Claude AI service under its
                  commercial data terms; copies uploaded there are deleted within 7 days. Your
                  documents are not sold or shared for any other purpose.
                  Review analytics (project type, finding counts, agency) are collected to improve
                  the platform. You may request deletion of your data at any time by contacting
                  ndayal@tggntx.com.
                </div>
                <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}>
                  <input type="checkbox" checked={reg.data_consent}
                    onChange={e => setReg({...reg, data_consent:e.target.checked})}
                    style={{ width:16, height:16, cursor:"pointer" }} />
                  <span style={{ fontSize:14, color:"#E6EDF3" }}>
                    I understand and accept the data handling terms above
                  </span>
                </label>
              </div>

              <button onClick={handleRegister}
                style={{ width:"100%", background:"#C8102E", color:"#FFF",
                         border:"none", borderRadius:8, padding:"14px 0",
                         fontSize:16, fontWeight:700, cursor:"pointer" }}>
                Start Free Trial →
              </button>

              <div style={{ textAlign:"center", marginTop:14, fontSize:13, color:"#484F58" }}>
                Already registered?{" "}
                <button onClick={() => { setError(null); setScreen("login"); }}
                  style={{ background:"none", border:"none", color:"#58A6FF",
                            cursor:"pointer", fontSize:13 }}>
                  Sign in here
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── LOGIN ── */}
        {screen === "reset" && (
          <div style={{ maxWidth:440, margin:"60px auto", padding:"0 20px" }}>
            <div style={{ background:"#161B22", border:"1px solid #30363D", borderRadius:12, padding:32 }}>
              <h2 style={{ color:"#E6EDF3", fontSize:22, marginTop:0, marginBottom:6 }}>Reset your password</h2>
              <p style={{ color:"#8B949E", fontSize:14, marginTop:0, marginBottom:22 }}>
                Choose a new password for your account.
              </p>
              <Field label="New password (min 12 characters, upper/lower/number/symbol)" value={resetPw} type="password"
                onChange={setResetPw} placeholder="New password" />
              <div style={{ height:14 }} />
              <Field label="Confirm new password" value={resetPw2} type="password"
                onChange={setResetPw2} placeholder="Re-enter password" />
              <button onClick={handleReset}
                style={{ width:"100%", marginTop:20, padding:"12px 0", border:"none", borderRadius:8,
                         background:"#1F6FEB", color:"#fff", fontSize:15, fontWeight:600, cursor:"pointer" }}>
                Set new password
              </button>
              {resetMsg && <div style={{ color:"#8B949E", fontSize:13, marginTop:14 }}>{resetMsg}</div>}
            </div>
          </div>
        )}
        {screen === "login" && (
          <div style={{ maxWidth:440, margin:"60px auto", padding:"0 20px" }}>
            <div style={{ background:"#161B22", border:"1px solid #30363D",
                          borderRadius:12, padding:32 }}>
              <h2 style={{ color:"#E6EDF3", fontSize:22, marginTop:0, marginBottom:6 }}>
                Sign in to PSEReviewAI
              </h2>
              <p style={{ color:"#8B949E", fontSize:14, marginTop:0, marginBottom:22 }}>
                Enter your email and password.
              </p>
              <Field label="Work Email" value={login.email} type="email"
                onChange={v => setLogin({...login, email:v})} placeholder="you@firm.com" />
              <div style={{ height:14 }} />
              <Field label="Password" value={login.password} type="password"
                onChange={v => setLogin({...login, password:v})} placeholder="Your password" />
              <div style={{ textAlign:"right", marginTop:8 }}>
                <button onClick={() => { setError(null); setForgotMode(!forgotMode); setForgotMsg(""); }}
                  style={{ background:"none", border:"none", color:"#58A6FF", cursor:"pointer", fontSize:12 }}>
                  Forgot password?
                </button>
              </div>
              {forgotMode && (
                <div style={{ marginTop:10, padding:12, background:"#0D1117", border:"1px solid #30363D", borderRadius:8 }}>
                  <Field label="Enter your email to reset" value={forgotEmail} type="email"
                    onChange={setForgotEmail} placeholder="you@firm.com" />
                  <button onClick={handleForgot}
                    style={{ width:"100%", marginTop:10, padding:"10px 0", border:"none", borderRadius:8,
                             background:"#2E6DA4", color:"#fff", fontSize:14, fontWeight:600, cursor:"pointer" }}>
                    Send reset link
                  </button>
                  {forgotMsg && <div style={{ color:"#8B949E", fontSize:12, marginTop:10 }}>{forgotMsg}</div>}
                </div>
              )}
              {error && (
                <div style={{ color:"#F85149", fontSize:13, marginTop:14 }}>{error}</div>
              )}
              <button onClick={handleLogin}
                style={{ width:"100%", marginTop:20, padding:"12px 0", border:"none",
                         borderRadius:8, background:"#1F6FEB", color:"#fff",
                         fontSize:15, fontWeight:600, cursor:"pointer" }}>
                Sign In →
              </button>
              <div style={{ textAlign:"center", marginTop:14, fontSize:13, color:"#484F58" }}>
                Need an account?{" "}
                <button onClick={() => { setError(null); setScreen("register"); }}
                  style={{ background:"none", border:"none", color:"#58A6FF",
                           cursor:"pointer", fontSize:13 }}>
                  Register here
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── TWO-FACTOR CODE ── */}
        {screen === "mfa" && (
          <div style={{ maxWidth:440, margin:"60px auto", padding:"0 20px" }}>
            <div style={{ background:"#161B22", border:"1px solid #30363D", borderRadius:12, padding:32 }}>
              <h2 style={{ color:"#E6EDF3", fontSize:22, marginTop:0, marginBottom:6 }}>Two-factor authentication</h2>
              <p style={{ color:"#8B949E", fontSize:14, marginTop:0, marginBottom:22 }}>
                Enter the 6-digit code from your authenticator app.
              </p>
              <Field label="Authentication code" value={mfaCode}
                onChange={v => setMfaCode(v.replace(/\D/g, "").slice(0, 6))} placeholder="123456" />
              {mfaMsg && <div style={{ color:"#F85149", fontSize:13, marginTop:14 }}>{mfaMsg}</div>}
              <button onClick={handleMfaVerify} disabled={mfaCode.length !== 6}
                style={{ width:"100%", marginTop:20, padding:"12px 0", border:"none", borderRadius:8,
                         background: mfaCode.length === 6 ? "#C8102E" : "#30363D", color:"#fff",
                         fontSize:15, fontWeight:600, cursor: mfaCode.length === 6 ? "pointer" : "not-allowed" }}>
                Verify →
              </button>
              <div style={{ textAlign:"center", marginTop:14 }}>
                <button onClick={() => { setMfaToken(null); setMfaMsg(""); setScreen("login"); }}
                  style={{ background:"none", border:"none", color:"#58A6FF", cursor:"pointer", fontSize:13 }}>
                  Back to sign in
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── TWO-FACTOR ENROLLMENT ── */}
        {screen === "mfa_enroll" && (
          <div style={{ maxWidth:480, margin:"60px auto", padding:"0 20px" }}>
            <div style={{ background:"#161B22", border:"1px solid #30363D", borderRadius:12, padding:32 }}>
              <h2 style={{ color:"#E6EDF3", fontSize:22, marginTop:0, marginBottom:6 }}>Set up two-factor authentication</h2>
              <p style={{ color:"#8B949E", fontSize:14, marginTop:0, marginBottom:22 }}>
                {mfaToken ? "Administrator accounts must use two-factor authentication. " : ""}
                Scan this QR code with an authenticator app (Microsoft Authenticator, Google Authenticator, Authy),
                then enter the 6-digit code it shows.
              </p>
              {mfaSetup ? (
                <>
                  <div style={{ background:"#FFFFFF", padding:16, borderRadius:8, width:"fit-content", margin:"0 auto 16px" }}>
                    <QRCodeSVG value={mfaSetup.otpauth_uri} size={180} />
                  </div>
                  <div style={{ fontSize:12, color:"#8B949E", marginBottom:18, textAlign:"center" }}>
                    Can't scan? Enter this key manually:<br/>
                    <code style={{ color:"#E6EDF3", fontSize:13, wordBreak:"break-all" }}>{mfaSetup.secret}</code>
                  </div>
                  <Field label="Code from your app" value={mfaCode}
                    onChange={v => setMfaCode(v.replace(/\D/g, "").slice(0, 6))} placeholder="123456" />
                  <button onClick={handleMfaConfirm} disabled={mfaCode.length !== 6}
                    style={{ width:"100%", marginTop:20, padding:"12px 0", border:"none", borderRadius:8,
                             background: mfaCode.length === 6 ? "#C8102E" : "#30363D", color:"#fff",
                             fontSize:15, fontWeight:600, cursor: mfaCode.length === 6 ? "pointer" : "not-allowed" }}>
                    Turn on two-factor →
                  </button>
                </>
              ) : (
                !mfaMsg && <div style={{ color:"#8B949E", fontSize:13 }}>Preparing…</div>
              )}
              {mfaMsg && <div style={{ color:"#F85149", fontSize:13, marginTop:14 }}>{mfaMsg}</div>}
              <div style={{ textAlign:"center", marginTop:14 }}>
                <button onClick={() => { const t = mfaToken; setMfaToken(null); setMfaSetup(null); setMfaMsg(""); setScreen(t || !user ? "login" : "upload"); }}
                  style={{ background:"none", border:"none", color:"#58A6FF", cursor:"pointer", fontSize:13 }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── MY REVIEWS ── */}
        {screen === "reviews" && user && (
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
              <div>
                <div style={{ fontSize:24, fontWeight:700 }}>My Reviews</div>
                <div style={{ color:"#A0AEC0", fontSize:14, marginTop:4 }}>
                  {reviewsList.length} {reviewsList.length === 1 ? "package" : "packages"}
                </div>
              </div>
              <button onClick={loadReviewsList} disabled={loadingReviews}
                style={{ background:"#21262D", color:"#C9D1D9", border:"1px solid #30363D",
                         borderRadius:6, padding:"8px 14px", fontSize:13,
                         cursor:loadingReviews?"not-allowed":"pointer" }}>
                {loadingReviews ? "Refreshing..." : "↻ Refresh"}
              </button>
            </div>

            {reviewsList.length === 0 && !loadingReviews && (
              <div style={{ ...cardStyle, textAlign:"center", padding:60 }}>
                <div style={{ fontSize:18, color:"#A0AEC0", marginBottom:12 }}>No reviews yet</div>
                <div style={{ color:"#6E7681", fontSize:14, marginBottom:20 }}>Upload your first PS&E package to begin</div>
                <button onClick={() => setScreen("upload")}
                  style={{ background:"#238636", color:"#FFF", border:"none", borderRadius:8,
                           padding:"10px 20px", fontSize:14, fontWeight:600, cursor:"pointer" }}>
                  Upload Package
                </button>
              </div>
            )}

            {reviewsList.length > 0 && (
              <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                {reviewsList.map(r => {
                  const isComplete = r.review_status === "COMPLETE";
                  const isRunning = r.review_status && r.review_status !== "COMPLETE" && r.review_status !== "FAILED";
                  const noReview = !r.review_id;
                  const statusColor = isComplete ? "#3FB950" : isRunning ? "#58A6FF" : noReview ? "#A0AEC0" : "#F85149";
                  const statusText = isComplete ? "Complete" : isRunning ? "Running" : noReview ? "Not reviewed" : "Failed";
                  return (
                    <div key={r.package_id}
                      onClick={() => openReview(r)}
                      style={{ ...cardStyle, cursor:"pointer", padding:18,
                               borderColor: isRunning ? "#1F4E8C" : "#30363D" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:16 }}>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontWeight:600, fontSize:15, marginBottom:6,
                                        whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                            {r.filename}
                          </div>
                          <div style={{ display:"flex", gap:16, fontSize:12, color:"#8B949E", flexWrap:"wrap" }}>
                            <span>{r.page_count || "?"} pages</span>
                            {r.csj && <span>CSJ: {r.csj}</span>}
                            {r.county && <span>{r.county}</span>}
                            {r.project_type && <span>{r.project_type}</span>}
                            <span>Uploaded {r.uploaded_at ? new Date(r.uploaded_at).toLocaleDateString() : "—"}</span>
                          </div>
                        </div>
                        <div style={{ textAlign:"right", flexShrink:0 }}>
                          <div style={{ display:"inline-flex", alignItems:"center", gap:6,
                                        background:statusColor + "22", color:statusColor,
                                        padding:"4px 10px", borderRadius:12, fontSize:11, fontWeight:600,
                                        marginBottom:8 }}>
                            {isRunning && <span style={{ width:6, height:6, borderRadius:"50%", background:statusColor, animation:"pulse 1.5s ease-in-out infinite" }}/>}
                            {statusText}
                          </div>
                          {isComplete && (
                            <div style={{ display:"flex", gap:10, fontSize:12 }}>
                              <span style={{ color:"#FF6B6B", fontWeight:600 }}>{r.critical_count || 0} crit</span>
                              <span style={{ color:"#FFB347", fontWeight:600 }}>{r.major_count || 0} maj</span>
                              <span style={{ color:"#FFD700", fontWeight:600 }}>{r.minor_count || 0} min</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── UPLOAD ── */}
        {screen === "upload" && (
          <div style={{ maxWidth:720, margin:"0 auto", paddingTop:40 }}>
            <div style={{ textAlign:"center", marginBottom:32 }}>
              <div style={{ fontSize:28, fontWeight:700, marginBottom:10 }}>
                PS&E Package Review
              </div>
              <div style={{ color:"#A0AEC0", marginBottom:8, fontSize:15 }}>
                Upload a PS&E package PDF to begin automated AI review
              </div>
              <div style={{ color:"#484F58", fontSize:13 }}>
                TxDOT · NTTA · FDOT · City of Houston · City of Austin · City of Dallas · and more
              </div>
            </div>

            {/* Review Type Selector */}
            <div style={{ marginBottom:32 }}>
              <div style={{ fontSize:11, letterSpacing:1, color:"#8B949E",
                            fontWeight:700, marginBottom:12, textTransform:"uppercase" }}>
                Review Type
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                {[
                  { id:"fresh", icon:"📋", title:"Fresh Review",
                    desc:"New submission — no prior comments. AI checks completeness, compliance, cross-references, and constructability." },
                  { id:"resolution", icon:"✅", title:"Comment Resolution",
                    desc:"Verify that prior review comments have been addressed in the current submittal. Upload the plans + prior comments spreadsheet." },
                ].map(opt => (
                  <div key={opt.id}
                    onClick={() => setReviewType(opt.id)}
                    style={{ border:`2px solid ${reviewType === opt.id ? "#58A6FF" : "#21262D"}`,
                             borderRadius:10, padding:"18px 20px", cursor:"pointer",
                             background: reviewType === opt.id ? "#0D1F33" : "#0D1117",
                             transition:"all 0.15s" }}>
                    <div style={{ fontSize:22, marginBottom:8 }}>{opt.icon}</div>
                    <div style={{ fontSize:14, fontWeight:700, marginBottom:6,
                                  color: reviewType === opt.id ? "#58A6FF" : "#C9D1D9" }}>
                      {opt.title}
                    </div>
                    <div style={{ fontSize:12, color:"#8B949E", lineHeight:1.5 }}>
                      {opt.desc}
                    </div>
                  </div>
                ))}
              </div>
              {reviewType === "resolution" && (
                <div style={{ marginTop:12, padding:"10px 14px", background:"#161B22",
                              border:"1px solid #30363D", borderRadius:8,
                              fontSize:12, color:"#8B949E", lineHeight:1.6 }}>
                  💡 <strong style={{ color:"#C9D1D9" }}>Resolution mode:</strong> Upload your
                  current plans (any submittal stage) in Step 1. Upload the prior round's
                  comment log in Step 2 — checkprint PDF, spreadsheet, or both.
                  PSEReviewAI will verify each comment against the current plans and
                  produce a resolution matrix.
                </div>
              )}
            </div>

            {/* Zone 1 — PS&E PDF (required) */}
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:11, letterSpacing:1, color:"#58A6FF",
                            fontWeight:700, marginBottom:8, textTransform:"uppercase" }}>
                Step 1 — Current PS&E Plans PDF <span style={{ color:"#F85149" }}>*</span>
              </div>
              <label style={{ display:"block", cursor: uploading ? "not-allowed" : "pointer" }}>
                <div style={{ border:`2px dashed ${uploading ? "#58A6FF" : "#30363D"}`,
                              borderRadius:12, padding:"48px 40px", background:"#161B22",
                              textAlign:"center", transition:"border-color 0.2s" }}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0];
                                 if (f) handleUpload({target:{files:[f]}}); }}>
                  {uploading ? (
                    <div>
                      <div style={{ fontSize:36, marginBottom:12 }}>⏳</div>
                      <div style={{ color:"#58A6FF", fontWeight:600, fontSize:15,
                                    marginBottom:6 }}>{uploadProgress}</div>
                      <div style={{ color:"#8B949E", fontSize:13 }}>
                        Large packages may take 3–5 minutes
                      </div>
                    </div>
                  ) : packageData ? (
                    <div>
                      <div style={{ fontSize:36, marginBottom:8 }}>✅</div>
                      <div style={{ color:"#3FB950", fontWeight:600, fontSize:15 }}>
                        Package loaded — {packageData.filename || packageData.project_name || "PS&E Package"}
                      </div>
                      <div style={{ color:"#8B949E", fontSize:12, marginTop:4 }}>
                        Click to replace
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize:36, marginBottom:12 }}>📄</div>
                      <div style={{ fontSize:16, fontWeight:600, marginBottom:6 }}>
                        Drop PS&E Package PDF here or click to browse
                      </div>
                      <div style={{ color:"#8B949E", fontSize:13 }}>
                        Any size · Ensure package includes an Index of Sheets
                      </div>
                    </div>
                  )}
                </div>
                <input type="file" accept=".pdf" onChange={handleUpload}
                  style={{ display:"none" }} disabled={uploading} />
              </label>
            </div>

            {/* Proceed to Resolution button — resolution mode, after PDF mapped */}
            {reviewType === "resolution" && packageData && (
              <div style={{ marginBottom:24 }}>
                <button onClick={() => {
                  // Merge spreadsheet comments if present
                  if (commentsRaw.length > 0) {
                    const merged = buildMergedComments().map((r, i) => ({
                      id: `sheet-${i}`,
                      sheet_number: r.sheet_ref || "—",
                      discipline: r.discipline || "GENERAL",
                      severity: "MAJOR",
                      comment_text: r.comment,
                      location_description: "",
                      resolution_status: "PENDING",
                      source: "spreadsheet",
                    }));
                    setComments(merged);
                  }
                  setScreen("checkprint");
                }}
                  style={{ width:"100%", background:"#C8102E", color:"#FFF",
                           border:"none", borderRadius:8, padding:"14px 0",
                           fontSize:15, fontWeight:700, cursor:"pointer" }}>
                  Proceed to Comment Resolution →
                </button>
                <div style={{ textAlign:"center", fontSize:12, color:"#484F58", marginTop:8 }}>
                  {checkprintFile ? `✅ Checkprint: ${checkprintFile.name}` : "No checkprint uploaded"}
                  {commentsRaw.length > 0 ? ` · ✅ ${buildMergedComments().length} spreadsheet comments` : ""}
                </div>
              </div>
            )}

            {/* Sign out footer */}
            {user && (
              <div style={{ textAlign:"center", fontSize:13, color:"#484F58" }}>
                Trial expires: {user.trial_expires_at?.slice(0,10)} ·{" "}
                <button onClick={() => { postLogout(); localStorage.removeItem("psereview_user"); localStorage.removeItem("psereview_token");
                  setUser(null); setScreen("register"); }}
                  style={{ background:"none", border:"none", color:"#484F58",
                           cursor:"pointer", fontSize:13, textDecoration:"underline" }}>
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── PACKAGE MAP ── */}
        {screen === "map" && packageData && (
          <div>
            <div style={{ display:"flex", justifyContent:"space-between",
                          alignItems:"flex-start", marginBottom:24 }}>
              <div>
                <div style={{ fontSize:24, fontWeight:700 }}>Package Map</div>
                <div style={{ color:"#A0AEC0", fontSize:14, marginTop:4 }}>
                  {packageData.filename} · {packageData.page_count} pages · {packageData.size_mb} MB
                </div>
              </div>
              <button onClick={() => setScreen(reviewType === "resolution" ? "checkprint" : "scope")}
                style={{ background:"#238636", color:"#FFF", border:"none",
                         borderRadius:8, padding:"10px 22px", fontSize:15,
                         fontWeight:600, cursor:"pointer" }}>
                {reviewType === "resolution" ? "Next: Comment Resolution →" : "Next: Set Project Scope →"}
              </button>
            </div>

            <div style={cardStyle}>
              <SectionLabel>Project Metadata</SectionLabel>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:20 }}>
                <MetaField label="Project Type" value={packageData.package_map?.project_type} highlight />
                <MetaField label="CSJ / Project Number" value={packageData.package_map?.csj_number} />
                <MetaField label="District" value={packageData.package_map?.txdot_district} />
                <MetaField label="County" value={packageData.package_map?.county} />
                <MetaField label="Highway" value={packageData.package_map?.highway} />
                <MetaField label="Letting Date" value={packageData.package_map?.letting_date} />
                <MetaField label="Consultant" value={packageData.package_map?.design_consultant} />
                <MetaField label="Confidence" value={packageData.package_map?.confidence_score
                  ? `${(packageData.package_map.confidence_score*100).toFixed(0)}%` : null} />
                <MetaField label="Sheets Found" value={packageData.package_map?.sheet_count} />
              </div>
            </div>

            <div style={{ display:"flex", gap:10, marginBottom:20 }}>
              <button onClick={remapPackage} disabled={remapping} style={{ background:"#1C2128", border:"1px solid #30363D", color:"#58A6FF", borderRadius:6, padding:"7px 16px", fontSize:13, cursor:remapping?"wait":"pointer", opacity:remapping?0.7:1 }}>{remapping ? "⏳ Re-mapping... (60-90s)" : "🔄 Re-map Package"}</button>
              <button onClick={deleteAndReupload} disabled={deletingPackage} style={{ background:"#2D1515", border:"1px solid #6E1A1A", color:"#FF6B6B", borderRadius:6, padding:"7px 16px", fontSize:13, cursor:"pointer", opacity:deletingPackage?0.6:1 }}>🗑️ {deletingPackage?"Deleting...":"Delete & Re-upload"}</button>
            </div>

            {packageData.package_map?.has_warning && packageData.package_map?.warning_message && (
              <div style={{ background:"#2D1B00", border:"1px solid #F0A500",
                            borderRadius:8, padding:"12px 16px", marginBottom:16,
                            display:"flex", alignItems:"flex-start", gap:12 }}>
                <span style={{ fontSize:18, lineHeight:1 }}>⚠️</span>
                <div>
                  <div style={{ color:"#F0A500", fontWeight:600, fontSize:14, marginBottom:4 }}>
                    Incomplete Package Map
                  </div>
                  <div style={{ color:"#C9A227", fontSize:13, lineHeight:1.5 }}>
                    {packageData.package_map.warning_message}
                  </div>
                  <div style={{ color:"#8B949E", fontSize:12, marginTop:6 }}>
                    For best results, upload a complete plan set that includes an Index of Sheets 
                    with page numbers listed. The review may flag missing segments that are actually 
                    present in the full plan set.
                  </div>
                </div>
              </div>
            )}
            <div style={cardStyle}>
              <SectionLabel>Package Segments</SectionLabel>
              {(packageData.package_map?.segments||[]).map((seg,i,arr) => (
                <div key={i} style={{ display:"flex", alignItems:"center", padding:"12px 0",
                                      borderBottom: i<arr.length-1 ? "1px solid #21262D":"none" }}>
                  <div style={{ width:240, fontSize:14, fontWeight:600 }}>
                    {({"GENERAL":"General","TRAFFIC_CONTROL_PLANS":"Traffic Control Plans","TRAFFIC_CONTROL_STANDARDS":"Traffic Control Standards","ROADWAY_PLANS":"Roadway & Removal Plans","ROADWAY_STANDARDS":"Roadway Standards","RETAINING_WALL_PLANS":"Retaining Wall Plans","RETAINING_WALL_STANDARDS":"Retaining Wall Standards","BRIDGE_PLANS":"Bridge Plans","BRIDGE_STANDARDS":"Bridge Standards","DRAINAGE_PLANS":"Drainage Plans","DRAINAGE_STANDARDS":"Drainage Standards","SIGNAL_PLANS":"Traffic Signal Plans","SIGNAL_STANDARDS":"Traffic Signal Standards","PAVEMENT_MARKING_PLANS":"Pavement Marking Plans","PAVEMENT_MARKING_STANDARDS":"Pavement Marking Standards","SIGNING_PLANS":"Signing Plans","SIGNING_STANDARDS":"Signing Standards","ILLUMINATION_PLANS":"Illumination Plans","ILLUMINATION_STANDARDS":"Illumination Standards","ITS_PLANS":"ITS Plans","ITS_STANDARDS":"ITS Standards","ENVIRONMENTAL_PLANS":"Environmental Plans","ENVIRONMENTAL_STANDARDS":"Environmental Standards","LANDSCAPING":"Landscape Amenities","ASSET_MAINTENANCE":"Asset Maintenance","TRAFFIC_CONTROL":"Traffic Control","BRIDGE":"Bridge","SIGNALS":"Signals","DRAINAGE":"Drainage","CROSS_SECTIONS":"Cross Sections","QUANTITIES":"Quantities","TITLE":"Title","INDEX":"Index","GENERAL_NOTES":"General Notes","TYPICAL_SECTIONS":"Typical Sections","PLAN_PROFILE":"Plan Profile","RETAINING_WALL":"Retaining Wall","SPECIAL_PROVISIONS":"Special Provisions","UTILITIES":"Utilities","UNKNOWN":"Unknown"})[seg.segment] || seg.segment.replace(/_/g," ")}
                  </div>
                  <div style={{ width:180, fontSize:13, color:"#A0AEC0" }}>
                    Pages {seg.page_start}–{seg.page_end}
                  </div>
                  <div style={{ fontSize:13, color:"#8B949E" }}>
                    {seg.sheet_count} sheet{seg.sheet_count!==1?"s":""}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── SCOPE ── */}
        {screen === "scope" && packageData && (
          <div>
            <div style={{ display:"flex", justifyContent:"space-between",
                          alignItems:"flex-start", marginBottom:24 }}>
              <div>
                <div style={{ fontSize:24, fontWeight:700 }}>Project Scope</div>
                <div style={{ color:"#A0AEC0", fontSize:14, marginTop:4 }}>
                  Provide context to calibrate findings and reduce false positives
                </div>
              </div>
              <button onClick={handleReview} disabled={reviewing}
                style={{ background: reviewing ? "#21262D":"#C8102E",
                         color: reviewing ? "#8B949E":"#FFF",
                         border:"none", borderRadius:8, padding:"10px 22px",
                         fontSize:15, fontWeight:600,
                         cursor: reviewing ? "not-allowed":"pointer" }}>
                {reviewing ? (reviewStatus || "Running Review...") : "▶ Run Review"}
              </button>
              {reviewing && (
                <div style={{ marginTop:12, padding:"12px 16px",
                              background:"#0D1117", borderRadius:8,
                              border:"1px solid #30363D" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                    <div style={{ width:12, height:12, borderRadius:"50%",
                                  background:"#58A6FF",
                                  animation:"pulse 1.5s ease-in-out infinite" }} />
                    <span style={{ color:"#58A6FF", fontSize:13, fontWeight:600 }}>
                      {reviewStatus || "Processing..."}
                    </span>
                  </div>
                  <div style={{ color:"#8B949E", fontSize:12 }}>
                    Full PE-level vision review in progress. Large plan sets (400+ pages) may take 1–2 hours. You will be notified by email when complete.
                  </div>
                  <div style={{ marginTop:10 }}>
                    <button onClick={stopReview} disabled={stopping}
                      style={{ background:"#2D1515", border:"1px solid #6E1A1A", color:"#FF6B6B",
                               borderRadius:6, padding:"6px 14px", fontSize:12, cursor:"pointer",
                               opacity:stopping?0.6:1 }}>
                      {stopping ? "Stopping..." : "⏹ Stop Review"}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div style={cardStyle}>
              <div style={{ marginBottom:24 }}>
                <label style={labelStyle}>Project Description</label>
                <textarea value={scope.description}
                  onChange={e => setScope({...scope, description:e.target.value})}
                  placeholder="e.g. Rural bridge replacement on county road, no ROW acquisition, single span box culvert, CE environmental clearance..."
                  style={{ width:"100%", background:"#0D1117", border:"1px solid #30363D",
                           borderRadius:6, padding:"12px 14px", color:"#E6EDF3",
                           fontSize:14, resize:"vertical", minHeight:90,
                           boxSizing:"border-box", lineHeight:1.6 }} />
                <div style={{ fontSize:13, color:"#8B949E", marginTop:6 }}>
                  If left blank, standard requirements for the detected project type and agency apply
                </div>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:24 }}>
                <div>
                  <label style={labelStyle}>Primary Agency / Owner</label>
                  <select value={scope.agency}
                    onChange={e => setScope({
                      ...scope,
                      agency: e.target.value,
                      additional_agencies: (scope.additional_agencies || []).filter(a => a !== e.target.value)
                    })}
                    style={selectStyle}>
                    {AGENCIES.map(([v,l]) => <option key={v} value={v} style={{background:"#0D1117"}}>{l}</option>)}
                  </select>
                  <div style={{ fontSize:13, color:"#8B949E", marginTop:6 }}>
                    The package owner. Findings cite this agency's manuals first.
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Submittal Stage</label>
                  <select value={scope.submittal_stage}
                    onChange={e => setScope({...scope, submittal_stage:e.target.value})}
                    style={selectStyle}>
                    {["Plans Adequate","30%","60%","90%","Final"].map(o =>
                      <option key={o} value={o} style={{background:"#0D1117"}}>{o}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn: "1 / span 2" }}>
                  <label style={labelStyle}>Additional Agencies (optional)</label>
                  <div style={{ fontSize:13, color:"#8B949E", marginBottom:8 }}>
                    For cross-jurisdictional projects. The package will also be reviewed against these agencies' standards. Each finding will be tagged with which agency flagged it.
                  </div>
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 8,
                    padding: 12,
                    background: "#0D1117",
                    border: "1px solid #30363D",
                    borderRadius: 6
                  }}>
                    {AGENCIES.filter(([v]) => v !== scope.agency && v !== "OTHER").map(([v, l]) => {
                      const checked = (scope.additional_agencies || []).includes(v);
                      return (
                        <label key={v} style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "6px 8px",
                          cursor: "pointer",
                          fontSize: 14,
                          color: checked ? "#C9D1D9" : "#8B949E"
                        }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={e => {
                              const current = scope.additional_agencies || [];
                              if (e.target.checked) {
                                setScope({ ...scope, additional_agencies: [...current, v] });
                              } else {
                                setScope({ ...scope, additional_agencies: current.filter(a => a !== v) });
                              }
                            }}
                          />
                          {l}
                        </label>
                      );
                    })}
                  </div>
                  {(scope.additional_agencies || []).length > 0 && (
                    <div style={{ fontSize:13, color:"#58A6FF", marginTop:6 }}>
                      Review will run against {1 + scope.additional_agencies.length} agencies. Expect proportionally longer review time and slightly higher cost.
                    </div>
                  )}
                </div>

                <Tristate label="ROW Acquisition Required?"
                  value={scope.row_required}
                  onChange={v => setScope({...scope, row_required:v})} />
                <Tristate label="Utility Conflicts / Relocations?"
                  value={scope.utilities_involved}
                  onChange={v => setScope({...scope, utilities_involved:v})} />
                <Tristate label="Lighting Required?"
                  value={scope.lighting_required}
                  onChange={v => setScope({...scope, lighting_required:v})} />
                <Tristate label="Phased Construction?"
                  value={scope.phased_construction}
                  onChange={v => setScope({...scope, phased_construction:v})} />

                <div>
                  <label style={labelStyle}>Environmental Clearance Type</label>
                  <select value={scope.environmental_clearance}
                    onChange={e => setScope({...scope, environmental_clearance:e.target.value})}
                    style={selectStyle}>
                    {["","CE (Categorical Exclusion)","EA (Environmental Assessment)",
                      "EIS (Environmental Impact Statement)","Unknown"].map(o =>
                      <option key={o} value={o} style={{background:"#0D1117"}}>{o||"Not specified"}</option>)}
                  </select>
                </div>
                <Tristate label="Design Exceptions Granted?"
                  value={scope.design_exceptions}
                  onChange={v => setScope({...scope, design_exceptions:v})} />
              </div>

              {/* Supporting Documents */}
              <div style={{ marginTop:28, paddingTop:28, borderTop:"1px solid #30363D" }}>
                <div style={{ fontSize:14, fontWeight:600, marginBottom:6 }}>
                  Supporting Documents{" "}
                  <span style={{ color:"#8B949E", fontWeight:400 }}>(optional)</span>
                </div>
                <div style={{ fontSize:13, color:"#8B949E", marginBottom:16 }}>
                  Upload project-specific documents to be referenced during review
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:14 }}>
                  {[["Project Scope / Design Criteria","SCOPE"],
                    ["General Notes","GENERAL_NOTES"],
                    ["Cross Sections","CROSS_SECTIONS"],
                    ["Special Provisions","SPECIAL_PROVISIONS"],
                    ["Geotechnical Report","GEOTECH"],
                    ["Environmental Document","ENVIRONMENTAL"],
                    ["Hydraulic Report","HYDRAULIC"],
                    ["Other Document","OTHER"],
                  ].map(([label, docType]) => (
                    <label key={docType} style={{ cursor: uploadingDoc ? "not-allowed":"pointer" }}>
                      <div style={{ background:"#0D1117", border:"1px dashed #30363D",
                                    borderRadius:8, padding:"12px 10px", textAlign:"center" }}>
                        <div style={{ fontSize:20, marginBottom:4 }}>📎</div>
                        <div style={{ fontSize:13, fontWeight:600, color:"#C9D1D9", marginBottom:2 }}>
                          {label}
                        </div>
                        <div style={{ fontSize:12, color:"#484F58" }}>Click to upload</div>
                      </div>
                      <input type="file" accept=".pdf,.doc,.docx,.xlsx"
                        disabled={uploadingDoc}
                        onChange={e => handleDocUpload(e, docType)}
                        style={{ display:"none" }} />
                    </label>
                  ))}
                </div>
                {documents.length > 0 && (
                  <div style={{ background:"#0D1117", borderRadius:8, padding:14 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"#8B949E",
                                  marginBottom:10, textTransform:"uppercase", letterSpacing:1 }}>
                      Uploaded Documents
                    </div>
                    {documents.map((doc,i) => (
                      <div key={i} style={{ display:"flex", alignItems:"center", gap:10,
                                            padding:"8px 0",
                                            borderBottom: i<documents.length-1 ? "1px solid #21262D":"none" }}>
                        <span>✅</span>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:14, fontWeight:500 }}>{doc.filename}</div>
                          <div style={{ fontSize:12, color:"#8B949E" }}>
                            {doc.doc_type} · {doc.size_mb} MB
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Scope summary */}
              <div style={{ marginTop:24, padding:16, background:"#0D1117",
                            borderRadius:8, border:"1px solid #21262D" }}>
                <div style={{ fontSize:12, color:"#8B949E", marginBottom:6,
                              fontWeight:600, textTransform:"uppercase", letterSpacing:1 }}>
                  Scope Summary
                </div>
                <div style={{ fontSize:14, color:"#C9D1D9", lineHeight:1.7 }}>
                  {[scope.description,
                    scope.submittal_stage && `${scope.submittal_stage} submittal`,
                    `Agency: ${scope.agency}`,
                    scope.row_required===false && "No ROW acquisition",
                    scope.row_required===true  && "ROW acquisition required",
                    scope.utilities_involved===false && "No utility conflicts",
                    scope.utilities_involved===true  && "Utility conflicts involved",
                    scope.lighting_required===false && "No lighting required",
                    scope.lighting_required===true  && "Lighting required",
                    scope.phased_construction===false && "Single phase construction",
                    scope.phased_construction===true  && "Phased construction",
                    scope.environmental_clearance && `Environmental: ${scope.environmental_clearance}`,
                    scope.design_exceptions===false && "No design exceptions",
                    scope.design_exceptions===true  && "Design exceptions granted",
                  ].filter(Boolean).join(". ") ||
                    "No scope provided — standard requirements will apply"}
                </div>
              </div>
            </div>
          </div>
        )}


        {/* ── MODULE 6 — COMMENT RESOLUTION ── */}
        {screen === "checkprint" && packageData && (
          <div>
            <div style={{ fontSize:24, fontWeight:700, marginBottom:6 }}>
              Module 6 — Comment Resolution Verification
            </div>
            <div style={{ color:"#A0AEC0", fontSize:14, marginBottom:28 }}>
              Upload the prior round's comment source — a marked-up checkprint PDF,
              a PDF comment table, or use the spreadsheet comments already loaded.
              PSEReviewAI will extract all comments and verify resolution against the current plans.
            </div>

            {/* Spreadsheet comments already loaded */}
            {comments.length > 0 && !checkprintId && (
              <div style={{ background:"#1A3A1A", border:"1px solid #3FB950",
                            borderRadius:8, padding:16, marginBottom:20,
                            display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontSize:24 }}>📊</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:15, fontWeight:600, color:"#3FB950" }}>
                    {comments.length} comments loaded from spreadsheet
                  </div>
                  <div style={{ fontSize:13, color:"#8B949E", marginTop:4 }}>
                    You can also upload a checkprint PDF below to add visual annotations
                  </div>
                </div>
                <button onClick={() => setScreen("checkprint")}
                  style={{ background:"#C8102E", color:"#FFF", border:"none",
                           borderRadius:6, padding:"8px 20px", fontSize:13,
                           fontWeight:600, cursor:"pointer" }}>
                  Skip to Resolution →
                </button>
              </div>
            )}

            {/* Step 1 — Upload checkprint */}
            {/* Unified comment source upload */}
            <div style={cardStyle}>
              <SectionLabel>Upload Prior Round Comments</SectionLabel>
              <div style={{ fontSize:14, color:"#8B949E", marginBottom:16 }}>
                Upload the prior round's comment source in any format —
                marked-up checkprint PDF, PDF comment table, Excel, or CSV.
                PSEReviewAI auto-detects the format.
              </div>
              <label style={{ display:"block", cursor:"pointer" }}>
                <div style={{ border:`2px dashed ${uploadingCheckprint ? "#58A6FF" : "#30363D"}`,
                              borderRadius:10, padding:"36px 32px",
                              background:"#0D1117", textAlign:"center" }}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => {
                    e.preventDefault();
                    const f = e.dataTransfer.files[0];
                    if (!f) return;
                    if (f.name.match(/\.(xlsx|csv)$/i)) {
                      handleSpreadsheetUpload({target:{files:[f]}});
                    } else {
                      handleCheckprintUpload({target:{files:[f]}});
                    }
                  }}>
                  {uploadingCheckprint ? (
                    <div>
                      <div style={{ fontSize:36, marginBottom:12 }}>⏳</div>
                      <div style={{ color:"#58A6FF", fontSize:15, fontWeight:600 }}>
                        {checkprintStatus === "PROCESSING"
                          ? "Extracting reviewer comments..."
                          : "Uploading..."}
                      </div>
                      <div style={{ color:"#8B949E", fontSize:13, marginTop:6 }}>
                        Auto-detecting format and extracting comments...
                      </div>
                    </div>
                  ) : checkprintId ? (
                    <div>
                      <div style={{ fontSize:36, marginBottom:8 }}>✅</div>
                      <div style={{ color:"#3FB950", fontWeight:600, fontSize:15 }}>
                        {comments.length} comments extracted from checkprint
                      </div>
                    </div>
                  ) : commentsFile ? (
                    <div>
                      <div style={{ fontSize:36, marginBottom:8 }}>📊</div>
                      <div style={{ color:"#3FB950", fontWeight:600, fontSize:14 }}>
                        {commentsFile.name}
                      </div>
                      <div style={{ color:"#8B949E", fontSize:12, marginTop:4 }}>
                        {buildMergedComments().length} comments mapped ·{" "}
                        <span style={{ color:"#58A6FF", cursor:"pointer",
                                       textDecoration:"underline" }}
                          onClick={e => { e.preventDefault(); setShowColMapper(true); }}>
                          Edit column mapping
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize:36, marginBottom:12 }}>📋</div>
                      <div style={{ fontSize:16, fontWeight:600, marginBottom:6 }}>
                        Drop comment file here or click to browse
                      </div>
                      <div style={{ color:"#8B949E", fontSize:13, marginBottom:4 }}>
                        Accepts: marked-up PDF · PDF comment table · .xlsx · .csv
                      </div>
                      <div style={{ color:"#484F58", fontSize:12 }}>
                        Format is auto-detected
                      </div>
                    </div>
                  )}
                </div>
                <input type="file" accept=".pdf,.xlsx,.csv"
                  onChange={e => {
                    const f = e.target.files[0];
                    if (!f) return;
                    if (f.name.match(/\.(xlsx|csv)$/i)) {
                      handleSpreadsheetUpload({target:{files:[f]}});
                    } else {
                      handleCheckprintUpload({target:{files:[f]}});
                    }
                  }}
                  style={{ display:"none" }}
                  disabled={uploadingCheckprint} />
              </label>

              {/* Load spreadsheet comments button */}
              {commentsFile && buildMergedComments().length > 0 && comments.length === 0 && (
                <button onClick={() => {
                  const merged = buildMergedComments().map((r, i) => ({
                    id: "sheet-" + i,
                    sheet_number: r.sheet_ref || "—",
                    discipline: r.discipline || "GENERAL",
                    severity: "MAJOR",
                    comment_text: r.comment,
                    location_description: "",
                    resolution_status: "PENDING",
                    source: "spreadsheet",
                  }));
                  setComments(merged);
                }}
                  style={{ marginTop:14, width:"100%", background:"#238636",
                           color:"#FFF", border:"none", borderRadius:8,
                           padding:"12px 0", fontSize:14, fontWeight:600,
                           cursor:"pointer" }}>
                  Load {buildMergedComments().length} Comments
                </button>
              )}
            </div>

            {/* Column mapper modal */}
            {showColMapper && spreadsheetHeaders.length > 0 && (
              <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)",
                            display:"flex", alignItems:"center", justifyContent:"center",
                            zIndex:1000 }}>
                <div style={{ background:"#161B22", border:"1px solid #30363D",
                              borderRadius:12, padding:32, width:480, maxWidth:"90vw" }}>
                  <div style={{ fontSize:16, fontWeight:700, marginBottom:6 }}>
                    Map Spreadsheet Columns
                  </div>
                  <div style={{ color:"#8B949E", fontSize:13, marginBottom:24 }}>
                    Tell PSEReviewAI which columns contain the review data.
                    Auto-detected — adjust if needed.
                  </div>
                  {[
                    { key:"number",     label:"Comment No.",            required:false },
                    { key:"sheet",      label:"Sheet / Drawing Number", required:false },
                    { key:"comment",    label:"Comment Text",           required:true  },
                    { key:"discipline", label:"Discipline / Category",  required:false },
                    { key:"response",   label:"Designer Response (recommended)", required:false },
                  ].map(({ key, label, required }) => (
                    <div key={key} style={{ marginBottom:16 }}>
                      <div style={{ fontSize:12, color:"#8B949E", marginBottom:6 }}>
                        {label}{required && <span style={{ color:"#F85149" }}> *</span>}
                      </div>
                      <select value={colMap?.[key] ?? ""}
                        onChange={e => setColMap(m => ({ ...m, [key]: e.target.value }))}
                        style={{ width:"100%", background:"#0D1117", border:"1px solid #30363D",
                                 color:"#C9D1D9", borderRadius:6, padding:"8px 10px",
                                 fontSize:13 }}>
                        <option value="">— not included —</option>
                        {spreadsheetHeaders.map((h, i) => (
                          <option key={i} value={String(i)}>{h}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                  <div style={{ display:"flex", gap:12, marginTop:24, justifyContent:"flex-end" }}>
                    <button onClick={() => {
                        setCommentsFile(null); setCommentsRaw([]);
                        setColMap(null); setShowColMapper(false);
                      }}
                      style={{ background:"transparent", border:"1px solid #30363D",
                               color:"#8B949E", padding:"8px 20px", borderRadius:6,
                               cursor:"pointer", fontSize:13 }}>
                      Remove File
                    </button>
                    <button onClick={() => setShowColMapper(false)}
                      style={{ background:"#238636", border:"none", color:"#fff",
                               padding:"8px 24px", borderRadius:6,
                               cursor:"pointer", fontSize:13, fontWeight:600 }}>
                      Confirm Mapping
                    </button>
                  </div>
                </div>
              </div>
            )}


            {/* Step 2 — Comment log */}
            {comments.length > 0 && (
              <div style={cardStyle}>
                <SectionLabel>
                  Extracted Comment Log ({comments.length} comments)
                </SectionLabel>

                {/* Summary by discipline */}
                <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:20 }}>
                  {Object.entries(
                    comments.reduce((acc, c) => {
                      acc[c.discipline] = (acc[c.discipline] || 0) + 1;
                      return acc;
                    }, {})
                  ).map(([disc, count]) => (
                    <span key={disc} style={{ background:"#0D1117",
                      border:"1px solid #30363D", borderRadius:6,
                      padding:"4px 10px", fontSize:12, color:"#C9D1D9" }}>
                      {disc.replace(/_/g," ")}: {count}
                    </span>
                  ))}
                </div>

                {/* Comment table */}
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse",
                                  fontSize:13 }}>
                    <thead>
                      <tr style={{ borderBottom:"2px solid #30363D",
                                   color:"#8B949E", textAlign:"left" }}>
                        <th style={{ padding:"8px 12px" }}>Sheet</th>
                        <th style={{ padding:"8px 12px" }}>Discipline</th>
                        <th style={{ padding:"8px 12px" }}>Severity</th>
                        <th style={{ padding:"8px 12px" }}>Comment</th>
                        <th style={{ padding:"8px 12px" }}>Location</th>
                        <th style={{ padding:"8px 12px" }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comments.map((c, i) => (
                        <tr key={i} style={{
                          borderBottom:"1px solid #21262D",
                          background: i % 2 === 0 ? "transparent" : "#0D1117"
                        }}>
                          <td style={{ padding:"8px 12px", fontWeight:600 }}>
                            {c.sheet_number || "—"}
                          </td>
                          <td style={{ padding:"8px 12px", color:"#8B949E" }}>
                            {(c.discipline||"").replace(/_/g," ")}
                          </td>
                          <td style={{ padding:"8px 12px" }}>
                            <span style={{
                              color: SEVERITY_COLOR[c.severity] || "#A0AEC0",
                              fontWeight:600, fontSize:11
                            }}>{c.severity}</span>
                          </td>
                          <td style={{ padding:"8px 12px", maxWidth:300,
                                       lineHeight:1.5 }}>
                            {c.comment_text}
                          </td>
                          <td style={{ padding:"8px 12px", color:"#8B949E",
                                       fontSize:12 }}>
                            {c.location_description || "—"}
                          </td>
                          <td style={{ padding:"8px 12px" }}>
                            <span style={{
                              background: c.resolution_status === "ADDRESSED"
                                ? "#1A3A1A" : "#2D2010",
                              color: c.resolution_status === "ADDRESSED"
                                ? "#3FB950" : "#FFB347",
                              padding:"2px 8px", borderRadius:4,
                              fontSize:11, fontWeight:600
                            }}>
                              {c.resolution_status || "PENDING"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Step 3 — Run resolution check */}
            {comments.length > 0 && (
              <div style={cardStyle}>
                <SectionLabel>Step 3 — Verify Resolution Against 90% Submittal</SectionLabel>
                <div style={{ fontSize:14, color:"#8B949E", marginBottom:20 }}>
                  Cross-reference each 60% comment against the 90% package map.
                  PSEReviewAI will flag: ✅ Addressed · ❌ Not Addressed · ⚠️ Partial · 🆕 New Issue
                </div>
                <button onClick={handleRunResolution}
                  disabled={runningResolution}
                  style={{ background: runningResolution ? "#21262D":"#C8102E",
                           color: runningResolution ? "#8B949E":"#FFF",
                           border:"none", borderRadius:8,
                           padding:"12px 28px", fontSize:15,
                           fontWeight:600,
                           cursor: runningResolution ? "not-allowed":"pointer" }}>
                  {runningResolution
                    ? "Running Resolution Check... (takes 5–10 minutes)"
                    : "▶ Run Resolution Verification"}
                </button>
              </div>
            )}

            {/* Resolution results */}
            {resolution && (
              <div style={cardStyle}>
                <SectionLabel>Resolution Report</SectionLabel>

                {/* Summary counts */}
                <div style={{ display:"flex", flexWrap:"wrap", gap:12, marginBottom:24 }}>
                  {[
                    ["✅ Addressed",     resolution.summary?.addressed,     "#3FB950"],
                    ["❌ Not Addressed", resolution.summary?.not_addressed,  "#FF6B6B"],
                    ["⚠️ Partial",       resolution.summary?.partial,        "#FFB347"],
                    ["🚫 Designer Disagreed", resolution.summary?.designer_disagreed, "#D29922"],
                    ["➖ Not Applicable", resolution.summary?.not_applicable, "#8B949E"],
                    ["📑 Outside Plans (report/memo)", resolution.summary?.outside_plans, "#A371F7"],
                    ["❓ Cannot Verify", resolution.summary?.cannot_verify,  "#8B949E"],
                    ["🆕 New Issues",    resolution.summary?.new_issues,     "#64B5F6"],
                  ].map(([label, count, color]) => (
                    <div key={label} style={{ flex:"1 1 130px", background:"#0D1117",
                                              border:`1px solid ${color}40`,
                                              borderRadius:8, padding:"14px 16px",
                                              textAlign:"center" }}>
                      <div style={{ fontSize:28, fontWeight:700,
                                    color }}>{count || 0}</div>
                      <div style={{ fontSize:12, color:"#8B949E",
                                    marginTop:4 }}>{label}</div>
                    </div>
                  ))}
                </div>

                {resolution.skipped_markups &&
                  (resolution.skipped_markups.META + resolution.skipped_markups.REPLY +
                   resolution.skipped_markups.DUPLICATE) > 0 && (
                  <div style={{ fontSize:12, color:"#8B949E", marginTop:-12, marginBottom:20 }}>
                    Not checked: {resolution.skipped_markups.META} checkmarks, highlights, stamps or printed text ·{" "}
                    {resolution.skipped_markups.REPLY} replies written on the markups ·{" "}
                    {resolution.skipped_markups.DUPLICATE} repeats. {resolution.comments_reviewed} review requests checked.
                  </div>
                )}

                {/* New issues */}
                {resolution.new_issues?.length > 0 && (
                  <div style={{ marginBottom:20 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:"#64B5F6",
                                  marginBottom:12 }}>
                      🆕 NEW ISSUES FOUND IN 90% SUBMITTAL
                    </div>
                    {resolution.new_issues.map((issue, i) => (
                      <div key={i} style={{ background:"#101D2D",
                                            border:"1px solid #64B5F640",
                                            borderRadius:8, padding:16,
                                            marginBottom:10 }}>
                        <div style={{ fontWeight:600, fontSize:15,
                                      marginBottom:6 }}>{issue.title}</div>
                        <div style={{ fontSize:13, color:"#C9D1D9",
                                      lineHeight:1.6 }}>{issue.description}</div>
                        {issue.sheet_reference && (
                          <div style={{ fontSize:12, color:"#8B949E",
                                        marginTop:6 }}>
                            📄 {issue.sheet_reference}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Resolution details */}
                {resolution.resolutions?.length > 0 && (
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:"#8B949E",
                                  marginBottom:12 }}>COMMENT RESOLUTION DETAILS</div>
                    {resolution.resolutions.map((res, i) => {
                      const comment = comments.find(c => c.id === res.comment_id);
                      const ST = {
                        ADDRESSED:          ["✅", "Addressed",          "#3FB950"],
                        NOT_ADDRESSED:      ["❌", "Not addressed",      "#FF6B6B"],
                        PARTIAL:            ["⚠️", "Partial",            "#FFB347"],
                        DESIGNER_DISAGREED: ["🚫", "Designer disagreed", "#D29922"],
                        NOT_APPLICABLE:     ["➖", "Not applicable",     "#8B949E"],
                        OUTSIDE_PLANS:      ["📑", "Outside plans",      "#A371F7"],
                        CANNOT_VERIFY:      ["❓", "Cannot verify",      "#8B949E"],
                      }[res.resolution_status] || ["❓", "Cannot verify", "#8B949E"];
                      const text = res.comment_text || comment?.comment_text || "Comment";
                      const head = [
                        res.comment_no ? `#${res.comment_no}` : null,
                        res.sheet_reference || (comment?.sheet_number && comment.sheet_number !== "—" ? comment.sheet_number : null),
                        res.discipline || null,
                      ].filter(Boolean).join("  ·  ");
                      return (
                        <div key={i} style={{ display:"flex", gap:12,
                                              padding:"14px 0",
                                              borderBottom:"1px solid #21262D",
                                              alignItems:"flex-start" }}>
                          <span style={{ fontSize:18, minWidth:24 }}>{ST[0]}</span>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ display:"flex", gap:8, alignItems:"center",
                                          flexWrap:"wrap", marginBottom:6 }}>
                              <span style={{ fontSize:11, fontWeight:700, color:ST[2],
                                             border:`1px solid ${ST[2]}60`, borderRadius:4,
                                             padding:"1px 6px" }}>{ST[1].toUpperCase()}</span>
                              {head && <span style={{ fontSize:12, color:"#8B949E" }}>{head}</span>}
                            </div>
                            <div style={{ fontSize:13, fontWeight:600, marginBottom:6,
                                          lineHeight:1.5 }}>{text}</div>
                            {res.read_as && res.read_as !== text && (
                              <div style={{ fontSize:12, color:"#C9D1D9", marginBottom:6 }}>
                                Markup reads (from the checkprint image): <em>{res.read_as}</em>
                              </div>
                            )}
                            {res.design_team_response && (
                              <div style={{ fontSize:12, color:"#C9D1D9", lineHeight:1.5,
                                            borderLeft:"2px solid #30363D", paddingLeft:8,
                                            marginBottom:6, fontStyle:"italic" }}>
                                Designer response: {res.design_team_response}
                              </div>
                            )}
                            <div style={{ fontSize:12, color:"#8B949E",
                                          lineHeight:1.5 }}>
                              <span style={{ color:"#C9D1D9", fontWeight:600 }}>Check: </span>
                              {res.resolution_notes}
                            </div>
                            {res.verified_in_sheet && (
                              <div style={{ fontSize:12, color:"#58A6FF",
                                            marginTop:4 }}>
                                📄 {res.verified_in_sheet}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── RESULTS ── */}
        {screen === "results" && reviewData && (
          <div>
            <div style={{ fontSize:24, fontWeight:700, marginBottom:6 }}>Review Results</div>
            <div style={{ color:"#A0AEC0", fontSize:14, marginBottom:28 }}>
              Modules: {(reviewData.modules_completed||[]).join(", ")} · {packageData?.filename}
            </div>

            <div style={{ display:"flex", gap:14, marginBottom:32 }}>
              {[["CRITICAL", reviewData.summary?.critical, "#FF6B6B"],
                ["MAJOR",    reviewData.summary?.major,    "#FFB347"],
                ["MINOR",    reviewData.summary?.minor,    "#64B5F6"],
              ].map(([sev,count,color]) => (
                <div key={sev} style={{ flex:1, background:"#161B22",
                                        border:`1px solid ${color}50`,
                                        borderRadius:10, padding:"18px 24px" }}>
                  <div style={{ fontSize:36, fontWeight:700, color }}>{count}</div>
                  <div style={{ fontSize:13, color:"#A0AEC0", marginTop:6,
                                fontWeight:600, letterSpacing:0.5 }}>{sev}</div>
                </div>
              ))}
            </div>

            {/* Feedback widget */}
            {!feedbackDone ? (
              <div style={{ background:"#161B22", border:"1px solid #30363D",
                            borderRadius:10, padding:22, marginBottom:24 }}>
                <div style={{ fontSize:15, fontWeight:600, marginBottom:14 }}>
                  Help us improve — how was this review?
                </div>
                <div style={{ display:"flex", gap:10, marginBottom:16 }}>
                  {[["👍 Useful", true], ["👎 Not useful", false]].map(([lbl,val]) => (
                    <button key={lbl} onClick={() => setFeedback({...feedback, useful:val})}
                      style={{ padding:"8px 18px", borderRadius:6, fontSize:14,
                               cursor:"pointer",
                               background: feedback.useful===val ? "#1A3A1A":"#0D1117",
                               color: feedback.useful===val ? "#3FB950":"#8B949E",
                               border:`1px solid ${feedback.useful===val ? "#3FB950":"#30363D"}` }}>
                      {lbl}
                    </button>
                  ))}
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginLeft:16 }}>
                    <span style={{ fontSize:13, color:"#8B949E" }}>False positives:</span>
                    <input type="number" min={0} max={50} value={feedback.fp}
                      onChange={e => setFeedback({...feedback, fp:parseInt(e.target.value)||0})}
                      style={{ width:60, background:"#0D1117", border:"1px solid #30363D",
                               borderRadius:4, padding:"6px 8px", color:"#E6EDF3",
                               fontSize:14 }} />
                  </div>
                </div>
                <textarea value={feedback.comments}
                  onChange={e => setFeedback({...feedback, comments:e.target.value})}
                  placeholder="What was missing or incorrect? (optional)"
                  style={{ width:"100%", background:"#0D1117", border:"1px solid #30363D",
                           borderRadius:6, padding:"10px 12px", color:"#E6EDF3",
                           fontSize:14, resize:"vertical", minHeight:70,
                           boxSizing:"border-box", marginBottom:12 }} />
                <button onClick={handleFeedback}
                  style={{ background:"#21262D", color:"#C9D1D9", border:"1px solid #30363D",
                           borderRadius:6, padding:"8px 18px", fontSize:14, cursor:"pointer" }}>
                  Submit Feedback
                </button>
              </div>
            ) : (
              <div style={{ background:"#1A3A1A", border:"1px solid #3FB950",
                            borderRadius:8, padding:14, marginBottom:24,
                            color:"#3FB950", fontSize:14 }}>
                ✅ Thank you for your feedback — it helps improve PSEReviewAI.
              </div>
            )}

            {(reviewData.findings||[]).map((f,i) => (
              <div key={i} style={{ background:SEVERITY_BG[f.severity]||"#161B22",
                                    border:`1px solid ${SEVERITY_COLOR[f.severity]}40`,
                                    borderRadius:10, padding:22, marginBottom:12 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10,
                              marginBottom:10, flexWrap:"wrap" }}>
                  <span style={{ background:SEVERITY_COLOR[f.severity]+"30",
                                 color:SEVERITY_COLOR[f.severity],
                                 fontSize:12, fontWeight:700, padding:"3px 10px",
                                 borderRadius:4, letterSpacing:0.5 }}>
                    {f.severity}
                  </span>
                  <span style={{ fontSize:13, color:"#8B949E", fontWeight:500 }}>
                    {f.module}
                  </span>
                  {f.sheet_reference && (
                    <span style={{ fontSize:13, color:"#A0AEC0", marginLeft:"auto" }}>
                      📄 {f.sheet_reference}
                    </span>
                  )}
                </div>
                <div style={{ fontWeight:700, fontSize:16, marginBottom:8,
                              lineHeight:1.4 }}>{f.title}</div>
                <div style={{ fontSize:14, color:"#C9D1D9", lineHeight:1.7,
                              marginBottom: f.recommendation ? 12:0 }}>
                  {f.description}
                </div>
                {f.recommendation && (
                  <div style={{ fontSize:14, color:"#58A6FF",
                                borderTop:"1px solid #30363D",
                                paddingTop:12, lineHeight:1.7 }}>
                    → {f.recommendation}
                  </div>
                )}
                {/* Finding-level feedback */}
                <div style={{ display:"flex", gap:8, marginTop:14,
                              paddingTop:12, borderTop:"1px solid #21262D",
                              alignItems:"center" }}>
                  <span style={{ fontSize:11, color:"#484F58", marginRight:4 }}>
                    Was this finding accurate?
                  </span>
                  {[
                    ["✅", "confirm",       "#3FB950", "Confirmed"],
                    ["❌", "false_positive", "#FF6B6B", "False positive"],
                    ["⚠️", "partial",        "#FFB347", "Partially correct"],
                  ].map(([icon, vote, color, label]) => (
                    <button key={vote}
                      onClick={() => handleFindingVote(f.id, vote)}
                      title={label}
                      style={{
                        background: findingVotes[f.id] === vote ? color+"22" : "transparent",
                        border: `1px solid ${findingVotes[f.id] === vote ? color : "#30363D"}`,
                        color: findingVotes[f.id] === vote ? color : "#484F58",
                        borderRadius:6, padding:"3px 10px", fontSize:12,
                        cursor:"pointer", transition:"all 0.15s"
                      }}>
                      {icon} {label}
                    </button>
                  ))}
                  {findingVotes[f.id] && (
                    <span style={{ fontSize:11, color:"#484F58", marginLeft:4 }}>
                      — recorded, thank you
                    </span>
                  )}
                </div>
              </div>
            ))}

            <button onClick={() => { setPackageData(null); setReviewData(null);
              setDocuments([]); setFeedback({useful:null,fp:0,missing:"",comments:""});
              setFeedbackDone(false); setFindingVotes({}); setCommentsFile(null); setCommentsRaw([]);
              setColMap(null); setCheckprintFile(null); setCheckprintId(null); setCheckprintStatus(null);
              setComments([]); setResolution(null); setReviewType("fresh");
              setScreen("upload"); }}
              style={{ marginTop:16, background:"#21262D", color:"#C9D1D9",
                       border:"1px solid #30363D", borderRadius:8,
                       padding:"12px 24px", fontSize:14, cursor:"pointer" }}>
              ← Review Another Package
            </button>
            <button onClick={async () => {
               try {
                 const tok = localStorage.getItem("psereview_token");
                 const resp = await fetch(`${API}/api/reviews/${reviewData?.review_id}/export/excel`, {
                   headers: { Authorization: `Bearer ${tok}` } });
                 if (!resp.ok) { alert("Export failed: " + resp.status); return; }
                 const blob = await resp.blob();
                 const url = window.URL.createObjectURL(blob);
                 const a = document.createElement("a");
                 a.href = url;
                 a.download = `PSEReview_${reviewData?.review_id}.xlsx`;
                 document.body.appendChild(a); a.click();
                 a.remove(); window.URL.revokeObjectURL(url);
               } catch (e) { alert("Export error: " + e.message); }
             }}
               style={{ background:"#1A6B1A", color:"#FFF", border:"none",
                        borderRadius:8, padding:"12px 24px", fontSize:14,
                        fontWeight:600, cursor:"pointer", textDecoration:"none",
                        display:"inline-flex", alignItems:"center", gap:8 }}>
              📊 Export to Excel
            </button>
            {tcpEnabled && (
              <button onClick={async () => {
                if (!packageData?.package_id) { alert("No package for this review."); return; }
                setTcpLoading(true);
                try {
                  const tok = localStorage.getItem("psereview_token");
                  const r = await fetch(`${API}/api/tcp/${packageData.package_id}/export`, {
                    method: "POST", headers: { Authorization: `Bearer ${tok}` } });
                  if (!r.ok) throw new Error("HTTP " + r.status);
                  const blob = await r.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `TrafficSequencing_${packageData.package_id.slice(0,8)}.docx`;
                  document.body.appendChild(a); a.click();
                  a.remove(); window.URL.revokeObjectURL(url);
                } catch (e) { alert("Traffic Sequencing generation failed. Please try again."); }
                finally { setTcpLoading(false); }
              }}
                style={{ background:"#2E6DA4", color:"#FFF", border:"none",
                         borderRadius:8, padding:"12px 24px", fontSize:14,
                         fontWeight:600, cursor:"pointer", display:"inline-flex",
                         alignItems:"center", gap:8 }}>
                {tcpLoading ? "Preparing…" : "🚦 Traffic Sequencing"}
              </button>
            )}
          </div>
        )}
        
      </div>
      {/* eslint-disable-next-line no-restricted-globals */}
      <PSEHelpAgent tab={screen} pd={packageData} rd={reviewData} />
    </div>
  );
}

// ── Reusable components ───────────────────────────────────────────────────────

const labelStyle = { fontSize:14, fontWeight:600, color:"#E6EDF3",
                     display:"block", marginBottom:8 };
const selectStyle = { width:"100%", background:"#0D1117", border:"1px solid #30363D",
                      borderRadius:6, padding:"10px 14px", color:"#E6EDF3", fontSize:14 };
const cardStyle   = { background:"#161B22", border:"1px solid #30363D",
                      borderRadius:10, padding:28, marginBottom:20 };

function SectionLabel({ children }) {
  return <div style={{ fontSize:12, fontWeight:700, color:"#8B949E",
                       textTransform:"uppercase", letterSpacing:1.2,
                       marginBottom:20 }}>{children}</div>;
}

function Field({ label, value, onChange, placeholder, type="text" }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input type={type} value={value} placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        style={{ width:"100%", background:"#0D1117", border:"1px solid #30363D",
                 borderRadius:6, padding:"10px 14px", color:"#E6EDF3",
                 fontSize:14, boxSizing:"border-box" }} />
    </div>
  );
}

function MetaField({ label, value, highlight }) {
  return (
    <div>
      <div style={{ fontSize:12, color:"#8B949E", marginBottom:6,
                    textTransform:"uppercase", letterSpacing:0.8, fontWeight:600 }}>
        {label}
      </div>
      <div style={{ fontSize:15, color: highlight ? "#58A6FF":"#E6EDF3",
                    fontWeight: highlight ? 700:400 }}>
        {value || "—"}
      </div>
    </div>
  );
}

function Tristate({ label, value, onChange }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <div style={{ display:"flex", gap:8 }}>
        {[["Yes",true,"#3FB950","#1A3A1A"],
          ["No",false,"#FF6B6B","#2D1515"],
          ["Unknown",null,"#8B949E","#161B22"]].map(([lbl,val,ac,ab]) => (
          <button key={lbl} onClick={() => onChange(val)}
            style={{ flex:1, padding:"10px 0", borderRadius:6, fontSize:14,
                     fontWeight: value===val ? 700:400, cursor:"pointer",
                     background: value===val ? ab:"#0D1117",
                     color: value===val ? ac:"#8B949E",
                     border:`1px solid ${value===val ? ac:"#30363D"}` }}>
            {lbl}
          </button>
        ))}
      </div>
    </div>
  );
}
// Wed Apr 22 02:37:04 PM UTC 2026


// PSEReviewAI Help Agent
// eslint-disable-next-line no-unused-vars
function PSEHelpAgent({ tab, pd, rd }) {
  var [open, setOpen] = useState(false);
  var [msgs, setMsgs] = useState([]);
  var [txt, setTxt] = useState("");
  var [busy, setBusy] = useState(false);
  var endRef = useRef(null);
  useEffect(function() {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);
  var hints = ({
    map: ["Why are some segments UNKNOWN?", "How does the package map work?"],
    scope: ["What should I enter for project scope?", "How does scope reduce false positives?"],
    results: ["What does a CRITICAL finding mean?", "How do I mark a false positive?"],
    m6: ["How does comment resolution work?", "What do markup colors mean?"],
  })[tab] || ["How do I upload a plan set?", "What agencies are supported?", "How long does a review take?"];
  function doSend() {
    if (!txt.trim() || busy) return;
    var msg = txt.trim();
    setTxt("");
    var history = msgs.concat([{ role: "user", content: msg }]);
    setMsgs(history);
    setBusy(true);
    var ctx = "Screen: " + tab;
    if (pd && pd.package_map) ctx += ", Project: " + (pd.package_map.project_type || "?");
    if (rd && rd.summary) ctx += ", " + rd.summary.critical + "C/" + rd.summary.major + "M";
    fetch("https://ca-psereviewai-backend.yellowsea-80606a88.eastus.azurecontainerapps.io/api/help/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ messages: history, context: ctx })
    }).then(function(r) { return r.json(); }).then(function(d) {
      setMsgs(history.concat([{ role: "assistant", content: d.reply || "Sorry, try again." }]));
      setBusy(false);
    }).catch(function() {
      setMsgs(history.concat([{ role: "assistant", content: "Connection error. Please try again." }]));
      setBusy(false);
    });
  }
  function onKey(e) { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); doSend(); } }
  return (
    <>
      <button onClick={function(){ setOpen(function(o){ return !o; }); }}
        style={{ position:"fixed", bottom:24, right:24, zIndex:9999, width:46, height:46,
                 borderRadius:"50%", background: open ? "#21262D" : "linear-gradient(135deg,#1A56DB,#06B6D4)",
                 border: open ? "1px solid #30363D" : "none",
                 boxShadow: open ? "none" : "0 4px 16px rgba(26,86,219,0.5)",
                 cursor:"pointer", color:"#fff", fontSize:20,
                 display:"flex", alignItems:"center", justifyContent:"center" }}>
        {open ? "✕" : "💬"}
      </button>
      {open && (
        <div style={{ position:"fixed", bottom:82, right:24, zIndex:9998, width:360, height:500,
                      background:"#0D1117", border:"1px solid #30363D", borderRadius:12,
                      display:"flex", flexDirection:"column", boxShadow:"0 8px 32px rgba(0,0,0,0.6)" }}>
          <div style={{ padding:"12px 16px", borderBottom:"1px solid #21262D",
                        display:"flex", alignItems:"center", gap:10,
                        background:"#0F1E35", borderRadius:"12px 12px 0 0" }}>
            <div style={{ width:30, height:30, borderRadius:"50%",
                          background:"linear-gradient(135deg,#1A56DB,#06B6D4)",
                          display:"flex", alignItems:"center", justifyContent:"center", fontSize:14 }}>🤖</div>
            <div>
              <div style={{ color:"#E6EDF3", fontWeight:700, fontSize:13 }}>PSEReviewAI Help</div>
              <div style={{ color:"#06B6D4", fontSize:11 }}>Ask me anything</div>
            </div>
            <button onClick={function(){ setMsgs([]); }}
              style={{ marginLeft:"auto", background:"none", border:"none", color:"#8B949E", cursor:"pointer", fontSize:12 }}>Clear</button>
          </div>
          <div style={{ flex:1, overflowY:"auto", padding:12, display:"flex", flexDirection:"column", gap:8 }}>
            {msgs.length === 0 && (
              <div style={{ color:"#8B949E", fontSize:13 }}>
                <div style={{ marginBottom:10 }}>Hi! I can help with findings, TxDOT standards, and platform questions.</div>
                {hints.map(function(h, i) {
                  return (
                    <button key={i} onClick={function(){ setTxt(h); }}
                      style={{ display:"block", width:"100%", textAlign:"left",
                               background:"#161B22", border:"1px solid #30363D", borderRadius:8,
                               padding:"7px 10px", color:"#CBD5E1", fontSize:12,
                               cursor:"pointer", marginBottom:5 }}>{h}</button>
                  );
                })}
              </div>
            )}
            {msgs.map(function(m, i) {
              return (
                <div key={i} style={{ display:"flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
                  <div style={{ maxWidth:"88%", padding:"8px 12px", fontSize:13, lineHeight:1.5,
                                borderRadius: m.role === "user" ? "12px 12px 4px 12px" : "12px 12px 12px 4px",
                                background: m.role === "user" ? "#1A56DB" : "#161B22",
                                border: m.role === "user" ? "none" : "1px solid #30363D",
                                color:"#E6EDF3" }}>{m.content}</div>
                </div>
              );
            })}
            {busy && (
              <div style={{ display:"flex" }}>
                <div style={{ background:"#161B22", border:"1px solid #30363D",
                              borderRadius:"12px 12px 12px 4px", padding:"10px 14px",
                              color:"#58A6FF", fontSize:13 }}>Thinking...</div>
              </div>
            )}
            <div ref={endRef} />
          </div>
          <div style={{ padding:"8px 10px", borderTop:"1px solid #21262D", display:"flex", gap:8,
                        background:"#0D1117", borderRadius:"0 0 12px 12px" }}>
            <textarea value={txt} onChange={function(e){ setTxt(e.target.value); }}
              onKeyDown={onKey} rows={2}
              placeholder="Ask about findings, standards, or how to use PSEReviewAI..."
              style={{ flex:1, background:"#161B22", border:"1px solid #30363D", borderRadius:8,
                       padding:"7px 10px", color:"#E6EDF3", fontSize:12,
                       resize:"none", fontFamily:"inherit" }} />
            <button onClick={doSend} disabled={!txt.trim() || busy}
              style={{ width:34, height:34, borderRadius:8, alignSelf:"flex-end",
                       background: (txt.trim() && !busy) ? "linear-gradient(135deg,#1A56DB,#06B6D4)" : "#21262D",
                       border:"none", cursor: (txt.trim() && !busy) ? "pointer" : "not-allowed",
                       color: (txt.trim() && !busy) ? "#fff" : "#8B949E", fontSize:16 }}>↑</button>
          </div>
        </div>
      )}
    </>
  );
}